import { buildLocalizedDefaultSeoSections } from './category-seo-defaults';
import { pavementSignCopy } from './stoepborden-category';
import { acousticCategoryCopy } from './akoestiek-category';
import { getReviewedFrenchCategoryCopy } from './category-copy-fr';
import { categoryCopyTranslations } from './category-copy-translations';

export type CategoryLocale = 'nl' | 'de' | 'en' | 'fr';

export type CategoryCopy = {
  label: string;
  title: string;
  subtitle: string;
  metaTitle: string;
  metaDescription: string;
  seoHeading?: string;
  seoIntro?: string;
  seoSections?: CategorySeoSection[];
};

export type CategorySeoSection = {
  heading: string;
  body: string;
  links?: Array<{ label: string; category?: string; productSlug?: string; href?: string }>;
};

export type CategoryDefinition = {
  slug: string;
  aliases?: string[];
  parentSlug?: string;
  group:
    | 'photo-wall'
    | 'stickers'
    | 'outdoor'
    | 'flags'
    | 'exhibition'
    | 'display'
    | 'materials'
    | 'print'
    | 'interior'
    | 'textile'
    | 'gifts'
    | 'packaging'
    | 'technology'
    | 'books'
    | 'sports'
    | 'accessories';
  providers: Array<'printcom' | 'probo' | 'prodigi' | 'gelato' | 'printful' | 'manual'>;
  copy: Record<CategoryLocale, CategoryCopy>;
};

const copy = (
  nl: CategoryCopy,
  de: CategoryCopy,
  en: CategoryCopy,
  fr: CategoryCopy,
): Record<CategoryLocale, CategoryCopy> => ({ nl, de, en, fr });

const basicCopy = (
  label: string,
  title: string,
  subtitle: string,
  metaTitle: string,
  metaDescription: string,
): Record<CategoryLocale, CategoryCopy> => copy(
  { label, title, subtitle, metaTitle, metaDescription },
  { label, title, subtitle, metaTitle, metaDescription },
  { label, title, subtitle, metaTitle, metaDescription },
  { label, title, subtitle, metaTitle, metaDescription },
);

export const categoryTaxonomy: CategoryDefinition[] = [
  {
    slug: 'printen-studio',
    aliases: ['ontwerpbare-objecten'],
    parentSlug: 'indoor-interior',
    group: 'interior',
    providers: ['probo', 'manual'],
    copy: copy(
      {
        label: 'Printen Studio',
        title: 'Woonobjecten en displays met jouw ontwerp',
        subtitle: 'Geef foto’s, patronen en je huisstijl een ruimtelijke vorm. Ontdek objecten met eigen bedrukking voor thuis, winkel en kantoor.',
        metaTitle: 'Printen Studio: objecten met eigen ontwerp | Printen.eu',
        metaDescription: 'Ontdek woonobjecten en presentatiedisplays met eigen print. Bekijk per product de materialen, ontwerpvlakken en montage bij Printen Studio.',
        seoIntro: 'Printen Studio brengt je eigen ontwerp van een plat beeld naar een object in de ruimte. Denk aan een persoonlijk patroon in je interieur of een merkontwerp op een presentatiedisplay. Bekijk per product welke vlakken je kunt bedrukken, hoe de onderdelen samenkomen en waarvoor het materiaal geschikt is. Afmetingen, montage, gebruik en bestandsvereisten verschillen per ontwerp en staan op de productpagina.',
      },
      {
        label: 'Printen Studio',
        title: 'Wohnobjekte und Displays mit Ihrem Design',
        subtitle: 'Geben Sie Fotos, Mustern und Ihrem Markenauftritt eine räumliche Form. Entdecken Sie individuell bedruckte Objekte für Zuhause, Geschäft und Büro.',
        metaTitle: 'Printen Studio: Objekte mit eigenem Design | Printen.eu',
        metaDescription: 'Entdecken Sie Wohnobjekte und Präsentationsdisplays mit eigenem Druck. Materialien, Gestaltungsflächen und Montage bei Printen Studio ansehen.',
        seoIntro: 'Printen Studio macht aus Ihrem Motiv ein Objekt im Raum. Ein persönliches Muster kann Ihr Zuhause ergänzen, ein Markendesign eine Produktpräsentation gestalten. Prüfen Sie bei jedem Produkt, welche Flächen bedruckbar sind, wie die Teile zusammengesetzt werden und für welchen Einsatz das Material geeignet ist. Maße, Montage, Nutzung und Dateianforderungen sind für jedes Design auf der Produktseite beschrieben.',
      },
      {
        label: 'Printen Studio',
        title: 'Interior objects and displays with your design',
        subtitle: 'Give photos, patterns and your brand identity a place in the room. Explore custom printed objects for homes, shops and offices.',
        metaTitle: 'Printen Studio: objects with your own design | Printen.eu',
        metaDescription: 'Explore interior objects and presentation displays with your own print. Check materials, design surfaces and assembly at Printen Studio.',
        seoIntro: 'Printen Studio takes your artwork from a flat image to an object in a room. A personal pattern can become part of your interior, while a brand design can shape a product presentation. Check each product for printable surfaces, assembly and suitable uses of the material. Dimensions, assembly instructions, usage and file requirements are specific to each design and are listed on its product page.',
      },
      {
        label: 'Printen Studio',
        title: 'Objets de décoration et présentoirs à votre image',
        subtitle: 'Donnez du relief à vos photos, motifs et à votre identité visuelle. Découvrez des objets imprimés pour la maison, la boutique et le bureau.',
        metaTitle: 'Printen Studio : objets avec votre création | Printen.eu',
        metaDescription: 'Découvrez des objets de décoration et des présentoirs avec votre impression. Consultez les matériaux, surfaces à créer et instructions de montage.',
        seoIntro: 'Printen Studio transforme votre visuel en un objet qui trouve sa place dans votre espace. Un motif personnel peut habiller votre intérieur et une création aux couleurs de votre marque peut accompagner une présentation. Vérifiez les surfaces imprimables, le montage et les usages adaptés au matériau pour chaque produit. Les dimensions, les instructions et les exigences de fichiers figurent sur sa page.',
      },
    ),
  },
  {
    slug: 'canvas',
    aliases: ['canvas-print', 'canvas-products'],
    parentSlug: 'wanddecoratie',
    group: 'photo-wall',
    providers: ['printcom', 'prodigi', 'gelato', 'manual'],
    copy: copy(
      {
        label: 'Foto op canvas',
        title: 'Foto op canvas bestellen',
        subtitle: 'Canvasprints voor foto, kunst en wanddecoratie. Kies formaat, randafwerking en upload direct je bestand.',
        metaTitle: 'Foto op canvas bestellen | Printen.eu',
        metaDescription: 'Bestel foto op canvas online bij Printen.eu. Kies formaat en afwerking, upload je foto en ontvang een scherpe canvasprint thuis.',
      },
      {
        label: 'Foto auf Leinwand',
        title: 'Foto auf Leinwand bestellen',
        subtitle: 'Leinwanddrucke für Fotos, Kunst und Wanddekoration. Format wählen, Datei hochladen und online bestellen.',
        metaTitle: 'Foto auf Leinwand bestellen | Printen.eu',
        metaDescription: 'Foto auf Leinwand online bestellen. Format und Ausführung wählen, Foto hochladen und direkt bei Printen.eu bestellen.',
      },
      {
        label: 'Photo canvas',
        title: 'Order photo canvas prints',
        subtitle: 'Canvas prints for photos, artwork and wall decoration. Choose a size, edge finish and upload your file.',
        metaTitle: 'Photo canvas prints | Printen.eu',
        metaDescription: 'Order photo canvas prints online. Choose size and finish, upload your image and receive a sharp canvas print.',
      },
      {
        label: 'Photo sur toile',
        title: 'Commander une photo sur toile',
        subtitle: 'Toiles imprimées pour photos, art et décoration murale. Choisissez le format et téléversez votre fichier.',
        metaTitle: 'Photo sur toile | Printen.eu',
        metaDescription: 'Commandez une photo sur toile en ligne. Choisissez le format, téléversez votre image et recevez une toile imprimée.',
      },
    ),
  },
  {
    slug: 'wanddecoratie',
    aliases: ['fotoprints', 'photo-wall', 'wall-art', 'acrylic', 'metallic', 'frames'],
    group: 'photo-wall',
    providers: ['printcom', 'probo', 'prodigi', 'gelato', 'manual'],
    copy: copy(
      {
        label: 'Wanddecoratie',
        title: 'Wanddecoratie met eigen foto of ontwerp',
        subtitle: 'Posters, plexiglas, dibond, aluminium, fototegels en panelen voor thuis, kantoor en winkel.',
        metaTitle: 'Wanddecoratie bestellen | Printen.eu',
        metaDescription: 'Bestel wanddecoratie met eigen foto of ontwerp: posters, plexiglas, dibond, aluminium en panelen. Online uploaden en thuisbezorgd.',
      },
      {
        label: 'Wanddekoration',
        title: 'Wanddekoration mit eigenem Motiv',
        subtitle: 'Poster, Acrylglas, Dibond, Aluminium, Fotokacheln und Paneele für Zuhause, Büro und Laden.',
        metaTitle: 'Wanddekoration bestellen | Printen.eu',
        metaDescription: 'Wanddekoration mit eigenem Foto oder Design bestellen: Poster, Acrylglas, Dibond, Aluminium und Paneele.',
      },
      {
        label: 'Wall decoration',
        title: 'Wall decoration with your own design',
        subtitle: 'Posters, acrylic, dibond, aluminium, photo tiles and panels for home, office and retail.',
        metaTitle: 'Wall decoration prints | Printen.eu',
        metaDescription: 'Order wall decoration with your own photo or design: posters, acrylic, dibond, aluminium and panels.',
      },
      {
        label: 'Decoration murale',
        title: 'Decoration murale personnalisee',
        subtitle: 'Posters, plexiglas, dibond, aluminium, carreaux photo et panneaux pour maison, bureau et commerce.',
        metaTitle: 'Decoration murale imprimee | Printen.eu',
        metaDescription: 'Commandez une décoration murale avec votre photo ou design: posters, plexiglas, dibond, aluminium et panneaux.',
      },
    ),
  },
  {
    slug: 'stickers-tapes',
    aliases: ['stickers', 'stickers-tape', 'labels'],
    group: 'stickers',
    providers: ['printcom', 'probo', 'manual'],
    copy: copy(
      {
        label: 'Stickers',
        title: 'Stickers laten drukken',
        subtitle: 'Vinylstickers, muurstickers, labels en contourstickers voor binnen, buiten en promotie.',
        metaTitle: 'Stickers laten drukken | Printen.eu',
        metaDescription: 'Stickers bestellen voor binnen en buiten. Upload je ontwerp en kies formaat, materiaal en afwerking.',
      },
      {
        label: 'Aufkleber',
        title: 'Aufkleber drucken lassen',
        subtitle: 'Vinylaufkleber, Wandsticker, Etiketten und Konturaufkleber für innen, außen und Werbung.',
        metaTitle: 'Aufkleber drucken lassen | Printen.eu',
        metaDescription: 'Aufkleber online bestellen. Design hochladen und Format, Material und Ausführung wählen.',
      },
      {
        label: 'Stickers',
        title: 'Custom sticker printing',
        subtitle: 'Vinyl stickers, wall stickers, labels and contour stickers for indoor, outdoor and promotion.',
        metaTitle: 'Custom stickers | Printen.eu',
        metaDescription: 'Order custom stickers online. Upload your design and choose size, material and finish.',
      },
      {
        label: 'Stickers',
        title: 'Impression de stickers',
        subtitle: 'Stickers vinyle, stickers muraux, etiquettes et stickers contour pour interieur, exterieur et promotion.',
        metaTitle: 'Stickers personnalises | Printen.eu',
        metaDescription: 'Commandez des stickers personnalisés. Téléversez votre design et choisissez format, matière et finition.',
      },
    ),
  },
  {
    slug: 'outdoor-advertising',
    aliases: ['reclame', 'buitenreclame', 'signage', 'banners', 'outdoor-living'],
    group: 'outdoor',
    providers: ['probo', 'printcom', 'manual'],
    copy: copy(
      {
        label: 'Buitenreclame',
        title: 'Buitenreclame en signing bestellen',
        subtitle: 'Spandoeken, lichtbakken, stoepborden, tuinposters en bewegwijzering voor opvallende zichtbaarheid.',
        metaTitle: 'Buitenreclame bestellen | Printen.eu',
        metaDescription: 'Bestel buitenreclame online: spandoeken, lichtbakken, stoepborden, tuinposters en bewegwijzering met eigen ontwerp.',
      },
      {
        label: 'Außenwerbung',
        title: 'Außenwerbung und Beschilderung bestellen',
        subtitle: 'Banner, Leuchtkästen, Kundenstopper, Gartenposter und Wegweiser für hohe Sichtbarkeit.',
        metaTitle: 'Außenwerbung bestellen | Printen.eu',
        metaDescription: 'Außenwerbung online bestellen: Banner, Leuchtkästen, Kundenstopper, Gartenposter und Beschilderung.',
      },
      {
        label: 'Outdoor advertising',
        title: 'Outdoor advertising and signage',
        subtitle: 'Banners, light boxes, pavement signs, garden posters and wayfinding for visible branding.',
        metaTitle: 'Outdoor advertising prints | Printen.eu',
        metaDescription: 'Order outdoor advertising online: banners, light boxes, pavement signs, garden posters and signage.',
      },
      {
        label: 'Publicite exterieure',
        title: 'Publicite exterieure et signaletique',
        subtitle: 'Baches, caissons lumineux, stop-trottoirs, posters jardin et signaletique pour etre visible.',
        metaTitle: 'Publicite exterieure | Printen.eu',
        metaDescription: 'Commandez publicite exterieure en ligne: baches, caissons lumineux, stop-trottoirs et signaletique.',
      },
    ),
  },
  {
    slug: 'paper-print',
    aliases: ['drukwerk', 'kaarten', 'flyers', 'brochures', 'folders', 'business-cards', 'letterheads'],
    group: 'print',
    providers: ['printcom', 'gelato', 'manual'],
    copy: copy(
      {
        label: 'Drukwerk',
        title: 'Drukwerk online bestellen',
        subtitle: 'Flyers, folders, visitekaartjes, posters, kaarten, briefpapier en bouwtekeningen voor zakelijk en particulier gebruik.',
        metaTitle: 'Drukwerk online bestellen | Printen.eu',
        metaDescription: 'Bestel drukwerk online: flyers, folders, visitekaartjes, posters, kaarten en bouwtekeningen. Upload je bestand en bestel direct.',
      },
      {
        label: 'Drucksachen',
        title: 'Drucksachen online bestellen',
        subtitle: 'Flyer, Falzblätter, Visitenkarten, Poster, Karten, Briefpapier und Bauzeichnungen.',
        metaTitle: 'Drucksachen online bestellen | Printen.eu',
        metaDescription: 'Drucksachen online bestellen: Flyer, Falzblätter, Visitenkarten, Poster, Karten und Bauzeichnungen.',
      },
      {
        label: 'Print products',
        title: 'Order print products online',
        subtitle: 'Flyers, folders, business cards, posters, cards, letterheads and construction drawings.',
        metaTitle: 'Print products online | Printen.eu',
        metaDescription: 'Order print products online: flyers, folders, business cards, posters, cards and construction drawings.',
      },
      {
        label: 'Imprimes',
        title: 'Commander des imprimes en ligne',
        subtitle: 'Flyers, depliants, cartes de visite, posters, cartes, papier a en-tete et plans.',
        metaTitle: 'Imprimes en ligne | Printen.eu',
        metaDescription: 'Commandez vos imprimes en ligne: flyers, depliants, cartes de visite, posters, cartes et plans.',
      },
    ),
  },
  {
    slug: 'indoor-interior',
    aliases: ['behang', 'wallpaper', 'interior'],
    group: 'interior',
    providers: ['probo', 'gelato', 'manual'],
    copy: copy(
      {
        label: 'Interieur',
        title: 'Interieurprints op maat',
        subtitle: 'Behang, deurmatten, vloerbeschermers en interieurprints met je eigen foto, patroon of ontwerp.',
        metaTitle: 'Interieurprints op maat | Printen.eu',
        metaDescription: 'Bestel interieurprints op maat: behang, deurmatten, vloerbeschermers en decoratie met eigen ontwerp.',
      },
      {
        label: 'Interieur',
        title: 'Interieurprints nach Mass',
        subtitle: 'Tapeten, Fußmatten, Bodenschutz und Interieurprints mit eigenem Foto, Muster oder Design.',
        metaTitle: 'Interieurprints nach Mass | Printen.eu',
        metaDescription: 'Interieurprints online bestellen: Tapeten, Fußmatten, Bodenschutz und Dekoration mit eigenem Design.',
      },
      {
        label: 'Interior prints',
        title: 'Custom interior prints',
        subtitle: 'Wallpaper, doormats, floor protection and interior prints with your own photo, pattern or design.',
        metaTitle: 'Custom interior prints | Printen.eu',
        metaDescription: 'Order custom interior prints: wallpaper, doormats, floor protection and decoration with your own design.',
      },
      {
        label: 'Interieur',
        title: 'Impressions interieur sur mesure',
        subtitle: 'Papier peint, paillassons, protections de sol et impressions intérieur avec votre photo ou design.',
        metaTitle: 'Impressions interieur | Printen.eu',
        metaDescription: 'Commandez des impressions interieur: papier peint, paillassons, protections de sol et decoration personnalisee.',
      },
    ),
  },
  {
    slug: 'floor-decoration',
    aliases: ['vloerdecoratie', 'floors', 'floor-protection'],
    group: 'interior',
    providers: ['probo', 'manual'],
    copy: copy(
      {
        label: 'Vloerdecoratie',
        title: 'Vloerdecoratie en vloerbescherming',
        subtitle: 'Bedrukte deurmatten, vloerbeschermers en vloerproducten voor entree, kantoor en interieur.',
        metaTitle: 'Vloerdecoratie bestellen | Printen.eu',
        metaDescription: 'Bestel vloerdecoratie en vloerbescherming online: deurmatten en vloerproducten met eigen ontwerp.',
      },
      {
        label: 'Bodendekoration',
        title: 'Bodendekoration und Bodenschutz',
        subtitle: 'Bedruckte Fußmatten, Bodenschutz und Bodenprodukte für Eingang, Büro und Interieur.',
        metaTitle: 'Bodendekoration bestellen | Printen.eu',
        metaDescription: 'Bodendekoration und Bodenschutz online bestellen: Fußmatten und Bodenprodukte mit eigenem Design.',
      },
      {
        label: 'Floor decoration',
        title: 'Floor decoration and protection',
        subtitle: 'Printed doormats, floor protection and floor products for entrances, offices and interiors.',
        metaTitle: 'Floor decoration prints | Printen.eu',
        metaDescription: 'Order floor decoration and protection online: doormats and floor products with your own design.',
      },
      {
        label: 'Decoration de sol',
        title: 'Decoration et protection de sol',
        subtitle: 'Paillassons imprimes, protections de sol et produits de sol pour entree, bureau et interieur.',
        metaTitle: 'Decoration de sol | Printen.eu',
        metaDescription: 'Commandez décoration et protection de sol: paillassons et produits de sol avec votre design.',
      },
    ),
  },
  {
    slug: 'textiel-cadeaus',
    aliases: ['textile-gifts', 'textiel', 'textiles', 'tote-bags'],
    group: 'textile',
    providers: ['probo', 'gelato', 'printful', 'manual'],
    copy: copy(
      {
        label: 'Textiel & cadeaus',
        title: 'Textiel en cadeaus bedrukken',
        subtitle: 'Handdoeken, strandlakens, dekens, draagtassen, kussens en cadeaus met eigen foto of ontwerp.',
        metaTitle: 'Textiel en cadeaus bedrukken | Printen.eu',
        metaDescription: 'Laat textiel en cadeaus bedrukken: handdoeken, strandlakens, dekens, draagtassen en kussens met eigen ontwerp.',
      },
      {
        label: 'Textilien & Geschenke',
        title: 'Textilien und Geschenke bedrucken',
        subtitle: 'Handtücher, Strandtücher, Decken, Taschen, Kissen und Geschenke mit eigenem Motiv.',
        metaTitle: 'Textilien und Geschenke bedrucken | Printen.eu',
        metaDescription: 'Textilien und Geschenke bedrucken lassen: Handtücher, Decken, Taschen und Kissen mit eigenem Design.',
      },
      {
        label: 'Textiles & gifts',
        title: 'Printed textiles and gifts',
        subtitle: 'Towels, beach towels, blankets, tote bags, cushions and gifts with your own photo or design.',
        metaTitle: 'Printed textiles and gifts | Printen.eu',
        metaDescription: 'Order printed textiles and gifts: towels, blankets, tote bags and cushions with your own design.',
      },
      {
        label: 'Textile & cadeaux',
        title: 'Textiles et cadeaux imprimes',
        subtitle: 'Serviettes, draps de plage, couvertures, sacs, coussins et cadeaux avec votre photo ou design.',
        metaTitle: 'Textiles et cadeaux imprimes | Printen.eu',
        metaDescription: 'Commandez textiles et cadeaux imprimes: serviettes, couvertures, sacs et coussins personnalises.',
      },
    ),
  },
  {
    slug: 'mokken',
    aliases: ['mugs', 'photo-mugs'],
    parentSlug: 'relatiegeschenken',
    group: 'gifts',
    providers: ['printcom', 'gelato', 'printful', 'manual'],
    copy: copy(
      {
        label: 'Fotomokken',
        title: 'Mok bedrukken met foto of logo',
        subtitle: 'Fotomokken en drinkwaren als cadeau, relatiegeschenk of merkartikel.',
        metaTitle: 'Mok bedrukken | Printen.eu',
        metaDescription: 'Laat een mok bedrukken met foto, tekst of logo. Bestel fotomokken online bij Printen.eu.',
      },
      {
        label: 'Fototassen',
        title: 'Tasse mit Foto oder Logo bedrucken',
        subtitle: 'Fototassen und Trinkwaren als Geschenk, Werbeartikel oder Markenprodukt.',
        metaTitle: 'Tasse bedrucken | Printen.eu',
        metaDescription: 'Tasse mit Foto, Text oder Logo bedrucken lassen. Fototassen online bestellen.',
      },
      {
        label: 'Photo mugs',
        title: 'Print mugs with photo or logo',
        subtitle: 'Photo mugs and drinkware as gifts, promotional items or branded products.',
        metaTitle: 'Custom photo mugs | Printen.eu',
        metaDescription: 'Print mugs with your photo, text or logo. Order custom photo mugs online.',
      },
      {
        label: 'Mugs photo',
        title: 'Mug personnalise avec photo ou logo',
        subtitle: 'Mugs photo et articles a boire comme cadeau, objet promotionnel ou produit de marque.',
        metaTitle: 'Mug personnalise | Printen.eu',
        metaDescription: 'Commandez un mug personnalise avec photo, texte ou logo. Impression en ligne.',
      },
    ),
  },
  {
    slug: 'keuken-interieur',
    aliases: ['keukenprints', 'inductiebeschermers', 'fototegels', 'tegels', 'spatwand', 'keuken-spatwand', 'kitchen-interior', 'kitchen', 'splashback'],
    parentSlug: 'indoor-interior',
    group: 'interior',
    providers: ['probo', 'printcom', 'manual'],
    copy: copy(
      {
        label: 'Keukenprints',
        title: 'Keukenprints met eigen foto, patroon of ontwerp',
        subtitle: 'Inductiebeschermers, fototegels en praktische keukenproducten die tegen dagelijks gebruik kunnen en tegelijk passen bij je interieur.',
        metaTitle: 'Keukenprints met eigen foto bestellen | Printen.eu',
        metaDescription: 'Bestel keukenprints zoals inductiebeschermers, fototegels en praktische producten met eigen foto, patroon of ontwerp.',
        seoHeading: 'Keukenprints en praktische interieurproducten op maat',
        seoIntro: 'Keukenprints verdienen een eigen plek, los van algemene interieurprints. In de keuken worden prints anders gebruikt dan aan de wand in een woonkamer of kantoor: ze moeten passen bij het interieur, maar ook bestand zijn tegen dagelijks gebruik, schoonmaken, warmte in de omgeving en veel contactmomenten. Daarom staat deze categorie bij Printen.eu apart klaar voor producten zoals inductiebeschermers, fototegels en andere praktische woonproducten met een eigen foto, illustratie, patroon of merkontwerp.',
        seoSections: [
          {
            heading: 'Waarom keukenprints een aparte subcategorie zijn',
            body: 'Een keukenprint is geen gewone wanddecoratie. Een poster, canvas of fotopaneel wordt vooral gekozen op uitstraling, formaat en materiaalgevoel. Bij een inductiebeschermer, tegel of praktisch keukenproduct spelen daarnaast gebruik, reiniging en plaatsing een grote rol. De print komt vaak terecht op een aanrecht, achterwand, kookzone, kast, plank of plek waar mensen dagelijks langs lopen. Daarom is het logisch om keukenprints als subcategorie onder interieur te behandelen, maar niet samen te voegen met de algemene categorie Interieur. Wie zoekt naar keukenprints wil meestal gericht een product dat mooi oogt en functioneel blijft in een keukenomgeving.',
          },
          {
            heading: 'Inductiebeschermers met eigen foto of patroon',
            body: 'Een inductiebeschermer is een goed voorbeeld van een product waarbij decoratie en bescherming samenkomen. Veel klanten willen geen standaard zwarte of transparante plaat, maar een ontwerp dat aansluit bij de keuken: marmerlook, betonstructuur, houtpatroon, familiefoto, illustratie, lijntekening, restaurantlogo of rustige kleurvlakken. Je kunt dus niet alleen een foto gebruiken, maar ook een patroon, huisstijlbestand of grafisch ontwerp. Let vooral op een rustige uitsnede, voldoende resolutie en een ontwerp dat ook mooi blijft wanneer het product dagelijks in het zicht ligt.',
          },
          {
            heading: 'Fototegels en keramische tegels',
            body: 'Tegels zijn een tweede logische groep binnen keukenprints. Ze kunnen decoratief worden gebruikt als losse fototegel, als cadeau, als kleine wanddecoratie of als onderdeel van een serie. Tegels passen ook goed bij keukens, horeca, vakantiehuizen en interieurprojecten waar een stevig, tastbaar product gewenst is. Een fototegel kan subtiel zijn met een patroon of juist persoonlijk met een foto, naam, quote of illustratie. Daardoor past hetzelfde product bij een keuken, eethoek, koffiehoek, restaurantinterieur of persoonlijk cadeau.',
          },
          {
            heading: 'Voor thuis, horeca en zakelijke interieurs',
            body: 'Keukenprints zijn niet alleen interessant voor particuliere klanten. Restaurants, lunchrooms, hotels, kantines, foodtrucks, zorglocaties en kantoren gebruiken praktische interieurproducten om een ruimte herkenbaar te maken. Een subtiele print met logo, patroon of sfeerbeeld kan een keuken, pantry of bar meer karakter geven zonder dat het meteen als reclame aanvoelt. Daarom worden ook horeca, bedrijfskeukens, koffiehoeken, balies, ontvangstruimtes en interieurs genoemd waar hygiene, uitstraling en herkenbaarheid samenkomen.',
          },
          {
            heading: 'Ontwerpen met foto, patroon, logo of rustige structuur',
            body: 'Niet elke keukenprint hoeft een drukke foto te zijn. Vaak werkt een rustige structuur beter: terrazzo, natuursteen, aquarel, lijnpatroon, botanische print, kleurvlak of herhalend patroon. Voor zakelijke klanten kan een logo of merkpatroon passend zijn, zolang het ontwerp niet te schreeuwerig wordt. De categorie moet bezoekers helpen om na te denken over contrast, uitsnede, verhouding en leesbaarheid. Een inductiebeschermer of tegel wordt meestal van dichtbij bekeken; kleine details mogen zichtbaar zijn, maar belangrijke tekst of gezichten moeten niet te dicht op de rand staan. Dat soort uitleg verhoogt de kwaliteit van uploads en verlaagt de kans op teleurstelling.',
          },
          {
            heading: 'Materiaal, afwerking en gebruikssituatie',
            body: 'Voor keukenproducten is het belangrijk om materiaal en afwerking goed te vergelijken. Een tegel voelt anders dan een plaat, paneel of beschermer. Sommige producten zijn bedoeld als decoratie, andere als praktische bescherming. Controleer daarom altijd de eigenschappen bij het product dat je kiest, zoals formaat, dikte, afwerking, reiniging en gebruikssituatie. Zo bestel je een keukenprint die past bij de plek waar je hem gebruikt en voorkom je dat je alleen op uiterlijk kiest.',
          },
          {
            heading: 'Keukenprints combineren met interieur',
            body: 'Een keukenprint staat meestal niet op zichzelf. De kleuren sluiten vaak aan op kastfronten, werkblad, tegels, vloer, muurverf of accessoires in de ruimte. Denk daarom vooraf na over de totale uitstraling: wil je een rustig geheel, een opvallend accent of juist een persoonlijk detail dat pas van dichtbij zichtbaar wordt? Een patroon kan mooi doorlopen in meerdere producten, terwijl een foto of logo beter werkt wanneer het genoeg vrije ruimte heeft. Door keukenprints te combineren met wanddecoratie, interieurprints of kleine cadeaus ontstaat een herkenbare stijl in huis, kantoor of horeca.',
          },
          {
            heading: 'Welke keukenprint past bij jouw gebruik',
            body: 'Kies eerst waar het product komt te liggen, staan of hangen. Een product op het aanrecht wordt anders gebruikt dan een decoratieve tegel aan de wand. Een keukenprint voor thuis mag persoonlijk en warm zijn, terwijl een print voor horeca of kantoor vaak rustiger, strakker en beter herhaalbaar moet zijn. Voor een cadeau draait het meestal om emotie en herkenning. Voor een zakelijk interieur gaat het vaker om merkgevoel, kleurconsistentie en een nette presentatie. Door die keuze eerst te maken, wordt het eenvoudiger om het juiste product, formaat en ontwerp te kiezen.',
          },
          {
            heading: 'Bestellen en uploaden',
            body: 'Bij het bestellen van keukenprints draait alles om een goede upload. Een foto moet scherp genoeg zijn voor het gekozen formaat, een patroon moet voldoende resolutie hebben en een logo wordt bij voorkeur als strak bestand aangeleverd. Plaats belangrijke tekst, gezichten en logos niet te dicht bij de rand. Controleer ook of het ontwerp in liggend of staand formaat beter werkt. Bij patronen is het slim om te letten op herhaling, schaal en rustige kleurvlakken, zodat het eindresultaat niet drukker wordt dan bedoeld.',
          },
          {
            heading: 'Voor cadeau, woning en zakelijke ruimte',
            body: 'Keukenprints zijn geschikt voor meer dan alleen een eigen keuken. Een fototegel kan een persoonlijk cadeau zijn voor een verjaardag, housewarming of jubileum. Een inductiebeschermer met patroon kan een rustige woonkeuken afmaken. Een subtiele print met logo kan juist passen in een koffiehoek, restaurant, lunchroom, hotelkamer of kantoorpantry. Door het ontwerp af te stemmen op de ruimte krijgt het product een duidelijke functie: decoreren, beschermen, persoonlijk maken of herkenbaarheid toevoegen.',
          },
        ],
      },
      {
        label: 'Küchendrucke',
        title: 'Küchendrucke mit eigenem Foto oder Design',
        subtitle: 'Induktionsschutz, Fotofliesen und praktische Küchenprodukte mit eigenem Motiv, Muster oder Logo.',
        metaTitle: 'Küchendrucke mit eigenem Foto | Printen.eu',
        metaDescription: 'Küchendrucke wie Induktionsschutz, Fotofliesen und praktische Produkte mit eigenem Foto oder Design bestellen.',
      },
      {
        label: 'Kitchen prints',
        title: 'Kitchen prints with your own photo or design',
        subtitle: 'Induction protectors, photo tiles and practical kitchen products with your own photo, pattern or logo.',
        metaTitle: 'Kitchen prints with your own photo | Printen.eu',
        metaDescription: 'Order kitchen prints such as induction protectors, photo tiles and practical products with your own design.',
      },
      {
        label: 'Impressions cuisine',
        title: 'Impressions cuisine avec photo ou design',
        subtitle: 'Protections induction, carreaux photo et produits cuisine pratiques avec votre photo, motif ou logo.',
        metaTitle: 'Impressions cuisine personnalisees | Printen.eu',
        metaDescription: 'Commandez impressions cuisine comme protections induction, carreaux photo et produits pratiques personnalises.',
      },
    ),
  },
  {
    slug: 'accessoires',
    aliases: ['accessories-posters', 'General', 'mounting-accessories', 'accessories'],
    group: 'accessories',
    providers: ['probo', 'printcom', 'gelato', 'manual'],
    copy: copy(
      {
        label: 'Accessoires',
        title: 'Print- en ophangaccessoires',
        subtitle: 'Ophangsystemen, afstandhouders, posterstrips, spanners, haken en montageproducten.',
        metaTitle: 'Print- en ophangaccessoires | Printen.eu',
        metaDescription: 'Bestel accessoires voor printproducten: ophangsystemen, afstandhouders, posterstrips, spanners en montageproducten.',
      },
      {
        label: 'Zubehör',
        title: 'Druck- und Montagezubehör',
        subtitle: 'Aufhängesysteme, Abstandhalter, Posterleisten, Spanner, Haken und Montageprodukte.',
        metaTitle: 'Druck- und Montagezubehör | Printen.eu',
        metaDescription: 'Zubehör für Druckprodukte bestellen: Aufhängesysteme, Abstandhalter, Posterleisten und Montageprodukte.',
      },
      {
        label: 'Accessories',
        title: 'Print and mounting accessories',
        subtitle: 'Hanging systems, spacers, poster strips, tensioners, hooks and mounting products.',
        metaTitle: 'Print and mounting accessories | Printen.eu',
        metaDescription: 'Order accessories for print products: hanging systems, spacers, poster strips and mounting products.',
      },
      {
        label: 'Accessoires',
        title: 'Accessoires impression et fixation',
        subtitle: 'Systemes de suspension, entretoises, rails poster, tendeurs, crochets et produits de montage.',
        metaTitle: 'Accessoires impression et fixation | Printen.eu',
        metaDescription: 'Commandez accessoires pour produits imprimes: suspension, entretoises, rails poster et montage.',
      },
    ),
  },
  {
    slug: 'vlaggen',
    aliases: ['flags', 'flaggen', 'beachflags', 'beach-flags', 'gevelvlaggen', 'pole-flags', 'flag-accessories'],
    group: 'flags',
    providers: ['probo', 'printcom', 'manual'],
    copy: copy(
      {
        label: 'Vlaggen',
        title: 'Vlaggen en beachflags bedrukken',
        subtitle: 'Beachflags, gevelvlaggen, mastvlaggen en vlagaccessoires voor winkels, events en buitenreclame.',
        metaTitle: 'Vlaggen en beachflags bedrukken | Printen.eu',
        metaDescription: 'Laat vlaggen en beachflags bedrukken met eigen ontwerp. Geschikt voor gevel, mast, evenement en winkelpresentatie.',
      },
      {
        label: 'Flaggen',
        title: 'Flaggen und Beachflags bedrucken',
        subtitle: 'Beachflags, Fassadenflaggen, Mastflaggen und Zubehör für Shops, Events und Außenwerbung.',
        metaTitle: 'Flaggen und Beachflags bedrucken | Printen.eu',
        metaDescription: 'Flaggen und Beachflags mit eigenem Design bedrucken lassen. Für Fassade, Mast, Event und Shop.',
      },
      {
        label: 'Flags',
        title: 'Custom printed flags and beach flags',
        subtitle: 'Beach flags, facade flags, pole flags and flag accessories for shops, events and outdoor advertising.',
        metaTitle: 'Custom flags and beach flags | Printen.eu',
        metaDescription: 'Order custom printed flags and beach flags with your own design for facades, poles, events and retail.',
      },
      {
        label: 'Drapeaux',
        title: 'Drapeaux et beach flags personnalises',
        subtitle: 'Beach flags, drapeaux de façade, drapeaux sur mat et accessoires pour magasins, events et publicité.',
        metaTitle: 'Drapeaux personnalises | Printen.eu',
        metaDescription: 'Commandez des drapeaux et beach flags personnalisés pour façade, mat, événement et point de vente.',
      },
    ),
  },
  {
    slug: 'beurs-evenement',
    aliases: ['trade-show', 'exhibition', 'beurswanden', 'event', 'events', 'roll-up-banners', 'festivaldoeken', 'zipper-banners'],
    group: 'exhibition',
    providers: ['probo', 'printcom', 'manual'],
    copy: copy(
      {
        label: 'Beurs & evenement',
        title: 'Beursmateriaal en eventprints',
        subtitle: 'Beurswanden, roll-up banners, festivaldoeken, eventvloeren en aankleding voor professionele presentaties.',
        metaTitle: 'Beursmateriaal en eventprints | Printen.eu',
        metaDescription: 'Bestel beursmateriaal online: beurswanden, roll-up banners, festivaldoeken, vloeren en eventaankleding met eigen ontwerp.',
      },
      {
        label: 'Messe & Event',
        title: 'Messebedarf und Eventdrucke',
        subtitle: 'Messewände, Roll-up-Banner, Festivalbanner, Eventböden und Ausstattung für Präsentationen.',
        metaTitle: 'Messebedarf und Eventdrucke | Printen.eu',
        metaDescription: 'Messebedarf online bestellen: Messewände, Roll-up-Banner, Eventboeden und Ausstattung mit eigenem Design.',
      },
      {
        label: 'Exhibition & event',
        title: 'Exhibition displays and event prints',
        subtitle: 'Exhibition walls, roll-up banners, festival banners, event floors and branded presentation materials.',
        metaTitle: 'Exhibition displays and event prints | Printen.eu',
        metaDescription: 'Order exhibition materials online: display walls, roll-up banners, festival banners, floors and event branding.',
      },
      {
        label: 'Salon & evenement',
        title: 'Supports salon et impressions evenement',
        subtitle: 'Murs de stand, roll-up banners, baches festival, sols evenementiels et habillage professionnel.',
        metaTitle: 'Supports salon et evenement | Printen.eu',
        metaDescription: 'Commandez supports de salon: murs de stand, roll-up banners, baches, sols et habillage evenementiel.',
      },
    ),
  },
  {
    slug: 'displays-pos',
    aliases: ['displays', 'pos', 'retail', 'presentation', 'presentatie', 'displaykarton', 'pavement-signs', 'pedestal-signs', 'retractable-banners', 'displays-pos-material'],
    group: 'display',
    providers: ['probo', 'printcom', 'prodigi', 'manual'],
    copy: copy(
      {
        label: 'Displays & POS',
        title: 'Displays en POS-materiaal bestellen',
        subtitle: 'Retaildisplays, presentatieborden, stoepborden, baliedisplays en POS-materiaal voor winkel en actie.',
        metaTitle: 'Displays en POS-materiaal | Printen.eu',
        metaDescription: 'Bestel displays en POS-materiaal online: retaildisplays, stoepborden, presentatieborden en actiemateriaal.',
      },
      {
        label: 'Displays & POS',
        title: 'Displays und POS-Material bestellen',
        subtitle: 'Retaildisplays, Präsentationsschilder, Kundenstopper und POS-Material für Shops und Aktionen.',
        metaTitle: 'Displays und POS-Material | Printen.eu',
        metaDescription: 'Displays und POS-Material online bestellen: Retaildisplays, Kundenstopper und Präsentationsmaterial.',
      },
      {
        label: 'Displays & POS',
        title: 'Order displays and POS materials',
        subtitle: 'Retail displays, presentation boards, pavement signs, counter displays and POS materials for campaigns.',
        metaTitle: 'Displays and POS materials | Printen.eu',
        metaDescription: 'Order displays and POS materials online: retail displays, pavement signs, boards and promotional materials.',
      },
      {
        label: 'Displays & PLV',
        title: 'Commander displays et PLV',
        subtitle: 'Displays retail, panneaux de presentation, stop-trottoirs, displays comptoir et PLV pour actions.',
        metaTitle: 'Displays et PLV | Printen.eu',
        metaDescription: 'Commandez displays et PLV: displays retail, stop-trottoirs, panneaux et supports promotionnels.',
      },
    ),
  },
  {
    slug: 'binnenreclame',
    aliases: ['indoor-advertising', 'wandreclame', 'wandbekleding', 'horeca', 'retail-signage'],
    group: 'display',
    providers: ['probo', 'printcom', 'manual'],
    copy: copy(
      {
        label: 'Binnenreclame',
        title: 'Binnenreclame en retail signing',
        subtitle: 'Wandreclame, wandbekleding, presentatiemateriaal, horeca signing en retailcommunicatie.',
        metaTitle: 'Binnenreclame bestellen | Printen.eu',
        metaDescription: 'Bestel binnenreclame en retail signing: wandreclame, wandbekleding, horeca- en presentatiemateriaal.',
      },
      {
        label: 'Innenwerbung',
        title: 'Innenwerbung und Retail-Signing',
        subtitle: 'Wandwerbung, Wandbekleidung, Präsentationsmaterial, Gastro-Signing und Retailkommunikation.',
        metaTitle: 'Innenwerbung bestellen | Printen.eu',
        metaDescription: 'Innenwerbung und Retail-Signing bestellen: Wandwerbung, Wandbekleidung und Präsentationsmaterial.',
      },
      {
        label: 'Indoor advertising',
        title: 'Indoor advertising and retail signage',
        subtitle: 'Wall advertising, wall covering, presentation materials, hospitality signage and retail communication.',
        metaTitle: 'Indoor advertising and retail signage | Printen.eu',
        metaDescription: 'Order indoor advertising and retail signage: wall graphics, wall covering and presentation materials.',
      },
      {
        label: 'Publicite interieure',
        title: 'Publicite interieure et signaletique retail',
        subtitle: 'Publicite murale, revetement mural, supports de presentation, horeca et communication retail.',
        metaTitle: 'Publicite interieure | Printen.eu',
        metaDescription: 'Commandez publicite interieure: visuels muraux, revetement mural et supports de presentation.',
      },
    ),
  },
  {
    slug: 'gevel-voertuig',
    aliases: ['gevelreclame', 'voertuigreclame', 'facade-signage', 'vehicle-graphics', 'window-stickers', 'raamstickers', 'real-estate-signs'],
    group: 'outdoor',
    providers: ['probo', 'printcom', 'manual'],
    copy: copy(
      {
        label: 'Gevel & voertuig',
        title: 'Gevelreclame en voertuigreclame',
        subtitle: 'Gevelbanners, raamstickers, vastgoedborden, magneetplaten en voertuigcommunicatie.',
        metaTitle: 'Gevelreclame en voertuigreclame | Printen.eu',
        metaDescription: 'Bestel gevelreclame en voertuigreclame: gevelbanners, raamstickers, vastgoedborden en magneetplaten.',
      },
      {
        label: 'Fassade & Fahrzeug',
        title: 'Fassadenwerbung und Fahrzeugwerbung',
        subtitle: 'Fassadenbanner, Fensteraufkleber, Immobilienschilder, Magnetschilder und Fahrzeugkommunikation.',
        metaTitle: 'Fassadenwerbung und Fahrzeugwerbung | Printen.eu',
        metaDescription: 'Fassadenwerbung und Fahrzeugwerbung bestellen: Banner, Fensteraufkleber, Schilder und Magnettafeln.',
      },
      {
        label: 'Facade & vehicle',
        title: 'Facade and vehicle graphics',
        subtitle: 'Facade banners, window stickers, real estate signs, magnetic signs and vehicle graphics.',
        metaTitle: 'Facade and vehicle graphics | Printen.eu',
        metaDescription: 'Order facade and vehicle graphics: banners, window stickers, real estate signs and magnetic signs.',
      },
      {
        label: 'Facade & vehicule',
        title: 'Publicite facade et vehicule',
        subtitle: 'Baches facade, stickers vitrine, panneaux immobiliers, supports magnetiques et marquage vehicule.',
        metaTitle: 'Publicite facade et vehicule | Printen.eu',
        metaDescription: 'Commandez publicite facade et vehicule: baches, stickers vitrine, panneaux et supports magnetiques.',
      },
    ),
  },
  {
    slug: 'plaatmateriaal',
    aliases: ['sign-material', 'sheet-material', 'plaat', 'forex', 'dibond', 'hpl', 'foamboard', 'display-board', 'corrugated-board', 're-board', 'pvc-signs'],
    group: 'materials',
    providers: ['probo', 'printcom', 'prodigi', 'manual'],
    copy: copy(
      {
        label: 'Plaatmateriaal',
        title: 'Plaatmateriaal bedrukken',
        subtitle: 'Forex, dibond, foamboard, displaykarton, HPL, kanaalplaat en andere plaatmaterialen voor sign en display.',
        metaTitle: 'Plaatmateriaal bedrukken | Printen.eu',
        metaDescription: 'Laat plaatmateriaal bedrukken: forex, dibond, foamboard, displaykarton, HPL en signmaterialen online bestellen.',
      },
      {
        label: 'Plattenmaterial',
        title: 'Plattenmaterial bedrucken',
        subtitle: 'Forex, Dibond, Foamboard, Displaykarton, HPL, Hohlkammerplatten und weitere Signmaterialien.',
        metaTitle: 'Plattenmaterial bedrucken | Printen.eu',
        metaDescription: 'Plattenmaterial bedrucken lassen: Forex, Dibond, Foamboard, Displaykarton, HPL und Signmaterialien.',
      },
      {
        label: 'Sign panels',
        title: 'Printed sign panels and board materials',
        subtitle: 'Forex, dibond, foamboard, display board, HPL, corrugated board and other sign materials.',
        metaTitle: 'Printed sign panels | Printen.eu',
        metaDescription: 'Order printed sign panels: forex, dibond, foamboard, display board, HPL and other board materials.',
      },
      {
        label: 'Panneaux rigides',
        title: 'Impression sur panneaux rigides',
        subtitle: 'Forex, dibond, foamboard, carton display, HPL, panneaux alveolaires et supports rigides.',
        metaTitle: 'Impression panneaux rigides | Printen.eu',
        metaDescription: 'Commandez impression sur panneaux rigides: forex, dibond, foamboard, HPL et supports signaletiques.',
      },
    ),
  },
  {
    slug: 'textielframes-doeken',
    aliases: ['textile-frames', 'textile-fabrics', 'textielframes', 'beursdoeken', 'outdoor-textielframe', 'acoustic-textile-frame'],
    group: 'materials',
    providers: ['probo', 'printcom', 'manual'],
    copy: copy(
      {
        label: 'Textielframes & doeken',
        title: 'Textielframes en textieldoeken',
        subtitle: 'Wandframes, vrijstaande frames, outdoor textielframes, beursdoeken en verwisselbare peesdoeken.',
        metaTitle: 'Textielframes en doeken | Printen.eu',
        metaDescription: 'Bestel textielframes en textieldoeken: wandframes, vrijstaande frames, outdoor frames en peesdoeken.',
      },
      {
        label: 'Textilrahmen & Stoffe',
        title: 'Textilrahmen und Textildrucke',
        subtitle: 'Wandrahmen, freistehende Rahmen, Outdoor-Textilrahmen, Messestoffe und wechselbare Textildrucke.',
        metaTitle: 'Textilrahmen und Stoffe | Printen.eu',
        metaDescription: 'Textilrahmen und Textildrucke bestellen: Wandrahmen, freistehende Rahmen und Outdoor-Textilrahmen.',
      },
      {
        label: 'Textile frames & fabrics',
        title: 'Textile frames and fabric prints',
        subtitle: 'Wall frames, freestanding frames, outdoor textile frames, exhibition fabrics and replaceable fabric prints.',
        metaTitle: 'Textile frames and fabric prints | Printen.eu',
        metaDescription: 'Order textile frames and fabric prints: wall frames, freestanding frames, outdoor frames and fabric graphics.',
      },
      {
        label: 'Cadres textiles & tissus',
        title: 'Cadres textiles et impressions tissu',
        subtitle: 'Cadres muraux, cadres autonomes, cadres textiles outdoor, tissus salon et visuels interchangeables.',
        metaTitle: 'Cadres textiles et tissus | Printen.eu',
        metaDescription: 'Commandez cadres textiles et impressions tissu: cadres muraux, autonomes, outdoor et visuels tissu.',
      },
    ),
  },
  {
    slug: 'verpakkingen',
    aliases: ['packaging', 'boxes', 'mailers', 'labels-packaging'],
    group: 'packaging',
    providers: ['printcom', 'manual'],
    copy: copy(
      {
        label: 'Verpakkingen',
        title: 'Verpakkingen en labels bedrukken',
        subtitle: 'Bedrukte verpakkingen, labels, stickers en drukwerk voor verzending, retail en merkbeleving.',
        metaTitle: 'Verpakkingen bedrukken | Printen.eu',
        metaDescription: 'Laat verpakkingen en labels bedrukken voor verzending, retail en branding. Online bestellen bij Printen.eu.',
      },
      {
        label: 'Verpackungen',
        title: 'Verpackungen und Labels bedrucken',
        subtitle: 'Bedruckte Verpackungen, Labels, Aufkleber und Drucksachen für Versand, Retail und Branding.',
        metaTitle: 'Verpackungen bedrucken | Printen.eu',
        metaDescription: 'Verpackungen und Labels für Versand, Retail und Branding bedrucken lassen.',
      },
      {
        label: 'Packaging',
        title: 'Custom packaging and labels',
        subtitle: 'Printed packaging, labels, stickers and print products for shipping, retail and branding.',
        metaTitle: 'Custom packaging and labels | Printen.eu',
        metaDescription: 'Order custom packaging and labels for shipping, retail and branding.',
      },
      {
        label: 'Emballages',
        title: 'Emballages et etiquettes personnalises',
        subtitle: 'Emballages imprimes, etiquettes, stickers et imprimes pour expedition, retail et marque.',
        metaTitle: 'Emballages personnalises | Printen.eu',
        metaDescription: 'Commandez emballages et etiquettes personnalises pour expedition, retail et branding.',
      },
    ),
  },
  {
    slug: 'technologie',
    aliases: ['technology', 'tech-accessories', 'phone-cases', 'tablet-cases', 'mouse-mats', 'laptop-sleeves', 'gaming-mats', 'watch-straps'],
    group: 'technology',
    providers: ['prodigi', 'gelato', 'printful', 'manual'],
    copy: copy(
      {
        label: 'Tech accessoires',
        title: 'Tech accessoires bedrukken',
        subtitle: 'Telefoonhoesjes, tabletcases, muismatten, laptop sleeves, gaming mats en watch straps met eigen ontwerp.',
        metaTitle: 'Tech accessoires bedrukken | Printen.eu',
        metaDescription: 'Bestel tech accessoires met eigen ontwerp: telefoonhoesjes, tabletcases, muismatten, laptop sleeves en gaming mats.',
      },
      {
        label: 'Tech-Zubehör',
        title: 'Tech-Zubehör bedrucken',
        subtitle: 'Handyhüllen, Tablet-Cases, Mauspads, Laptop Sleeves, Gaming Mats und Watch Straps.',
        metaTitle: 'Tech-Zubehör bedrucken | Printen.eu',
        metaDescription: 'Tech-Zubehör mit eigenem Design bestellen: Handyhüllen, Mauspads, Laptop Sleeves und Gaming Mats.',
      },
      {
        label: 'Tech accessories',
        title: 'Custom tech accessories',
        subtitle: 'Phone cases, tablet cases, mouse mats, laptop sleeves, gaming mats and watch straps with your design.',
        metaTitle: 'Custom tech accessories | Printen.eu',
        metaDescription: 'Order custom tech accessories: phone cases, tablet cases, mouse mats, laptop sleeves and gaming mats.',
      },
      {
        label: 'Accessoires tech',
        title: 'Accessoires tech personnalises',
        subtitle: 'Coques téléphone, étuis tablette, tapis souris, housses laptop, gaming mats et bracelets montre.',
        metaTitle: 'Accessoires tech personnalises | Printen.eu',
        metaDescription: 'Commandez accessoires tech personnalises: coques, tapis souris, housses laptop et gaming mats.',
      },
    ),
  },
  {
    slug: 'boeken-magazines',
    aliases: ['books-magazines', 'books', 'magazines', 'photo-books', 'hardcover-photo-book', 'softcover-photo-book', 'layflat-photo-book'],
    group: 'books',
    providers: ['prodigi', 'gelato', 'manual'],
    copy: copy(
      {
        label: 'Boeken & magazines',
        title: 'Boeken, magazines en fotoboeken drukken',
        subtitle: 'Fotoboeken, magazines, journals en publicaties voor makers, fotografen en bedrijven.',
        metaTitle: 'Boeken en magazines drukken | Printen.eu',
        metaDescription: 'Bestel boeken, magazines en fotoboeken online. Geschikt voor portfolio, publicatie, cadeau en zakelijke presentatie.',
      },
      {
        label: 'Buecher & Magazine',
        title: 'Bücher, Magazine und Fotobücher drucken',
        subtitle: 'Fotobücher, Magazine, Journals und Publikationen für Kreative, Fotografen und Unternehmen.',
        metaTitle: 'Bücher und Magazine drucken | Printen.eu',
        metaDescription: 'Bücher, Magazine und Fotobücher online bestellen. Für Portfolio, Publikation, Geschenk und Präsentation.',
      },
      {
        label: 'Books & magazines',
        title: 'Print books, magazines and photo books',
        subtitle: 'Photo books, magazines, journals and publications for creators, photographers and businesses.',
        metaTitle: 'Print books and magazines | Printen.eu',
        metaDescription: 'Order books, magazines and photo books online for portfolios, publications, gifts and presentations.',
      },
      {
        label: 'Livres & magazines',
        title: 'Imprimer livres, magazines et livres photo',
        subtitle: 'Livres photo, magazines, journals et publications pour createurs, photographes et entreprises.',
        metaTitle: 'Imprimer livres et magazines | Printen.eu',
        metaDescription: 'Commandez livres, magazines et livres photo en ligne pour portfolio, publication, cadeau et presentation.',
      },
    ),
  },
  {
    slug: 'home-living',
    aliases: ['homeware', 'home-living', 'home-and-living', 'bath-mats', 'curtains', 'wall-tapestries', 'clocks', 'blankets', 'pillows', 'rugs', 'placemats'],
    group: 'gifts',
    providers: ['prodigi', 'gelato', 'printful', 'manual'],
    copy: copy(
      {
        label: 'Home & living',
        title: 'Home & living producten bedrukken',
        subtitle: 'Kussens, dekens, wandkleden, klokken, placemats, badmatten en woonproducten met eigen design.',
        metaTitle: 'Home & living producten bedrukken | Printen.eu',
        metaDescription: 'Bestel home & living producten met eigen ontwerp: kussens, dekens, wandkleden, klokken, placemats en badmatten.',
      },
      {
        label: 'Home & Living',
        title: 'Home & Living Produkte bedrucken',
        subtitle: 'Kissen, Decken, Wandteppiche, Uhren, Platzsets, Badematten und Wohnprodukte mit eigenem Design.',
        metaTitle: 'Home & Living Produkte bedrucken | Printen.eu',
        metaDescription: 'Home & Living Produkte mit eigenem Design bestellen: Kissen, Decken, Wandteppiche, Uhren und Badematten.',
      },
      {
        label: 'Home & living',
        title: 'Custom home and living products',
        subtitle: 'Cushions, blankets, wall tapestries, clocks, placemats, bath mats and home products with your design.',
        metaTitle: 'Custom home and living products | Printen.eu',
        metaDescription: 'Order custom home and living products: cushions, blankets, wall tapestries, clocks, placemats and bath mats.',
      },
      {
        label: 'Maison & deco',
        title: 'Produits maison personnalises',
        subtitle: 'Coussins, couvertures, tentures murales, horloges, sets de table, tapis de bain et produits deco.',
        metaTitle: 'Produits maison personnalises | Printen.eu',
        metaDescription: 'Commandez produits maison personnalises: coussins, couvertures, tentures, horloges et tapis de bain.',
      },
    ),
  },
  {
    slug: 'sport-spel',
    aliases: ['sport-games', 'sports-games', 'sport-and-games', 'games', 'gym-equipment'],
    group: 'sports',
    providers: ['prodigi', 'manual'],
    copy: copy(
      {
        label: 'Sport & spel',
        title: 'Sport- en spelproducten personaliseren',
        subtitle: 'Sportaccessoires, games en promotionele producten met eigen ontwerp of merk.',
        metaTitle: 'Sport en spel personaliseren | Printen.eu',
        metaDescription: 'Bestel gepersonaliseerde sport- en spelproducten met eigen ontwerp, foto of merkuitstraling.',
      },
      {
        label: 'Sport & Spiele',
        title: 'Sport- und Spielprodukte personalisieren',
        subtitle: 'Sportzubehör, Spiele und Promotionprodukte mit eigenem Design oder Marke.',
        metaTitle: 'Sport und Spiele personalisieren | Printen.eu',
        metaDescription: 'Personalisierte Sport- und Spielprodukte mit eigenem Design, Foto oder Marke bestellen.',
      },
      {
        label: 'Sport & games',
        title: 'Personalised sport and game products',
        subtitle: 'Sports accessories, games and promotional products with your design or brand.',
        metaTitle: 'Personalised sport and games | Printen.eu',
        metaDescription: 'Order personalised sport and game products with your design, photo or brand.',
      },
      {
        label: 'Sport & jeux',
        title: 'Produits sport et jeux personnalises',
        subtitle: 'Accessoires sport, jeux et produits promotionnels avec votre design ou marque.',
        metaTitle: 'Sport et jeux personnalises | Printen.eu',
        metaDescription: 'Commandez produits sport et jeux personnalisés avec votre design, photo ou marque.',
      },
    ),
  },
  {
    slug: 'fotoboeken',
    aliases: ['photo-books', 'photobooks'],
    parentSlug: 'boeken-magazines',
    group: 'gifts',
    providers: ['gelato', 'printcom', 'manual'],
    copy: copy(
      {
        label: 'Fotoboeken',
        title: 'Fotoboek maken en bestellen',
        subtitle: 'Fotoboeken en albums voor vakanties, familie, portfolio en zakelijke presentaties.',
        metaTitle: 'Fotoboek maken | Printen.eu',
        metaDescription: 'Maak en bestel je fotoboek online. Upload je foto’s en kies formaat, papier en afwerking.',
      },
      {
        label: 'Fotobuecher',
        title: 'Fotobuch erstellen und bestellen',
        subtitle: 'Fotobücher und Alben für Urlaub, Familie, Portfolio und Präsentationen.',
        metaTitle: 'Fotobuch erstellen | Printen.eu',
        metaDescription: 'Fotobuch online erstellen und bestellen. Fotos hochladen und Format, Papier und Ausführung wählen.',
      },
      {
        label: 'Photo books',
        title: 'Create and order photo books',
        subtitle: 'Photo books and albums for holidays, family, portfolios and business presentations.',
        metaTitle: 'Create photo books | Printen.eu',
        metaDescription: 'Create and order photo books online. Upload photos and choose size, paper and finish.',
      },
      {
        label: 'Livres photo',
        title: 'Creer et commander un livre photo',
        subtitle: 'Livres photo et albums pour vacances, famille, portfolio et presentations.',
        metaTitle: 'Livre photo personnalise | Printen.eu',
        metaDescription: 'Créez et commandez votre livre photo en ligne. Téléversez vos photos et choisissez format et papier.',
      },
    ),
  },
  {
    slug: 'kalenders',
    aliases: ['calendars'],
    parentSlug: 'paper-print',
    group: 'gifts',
    providers: ['gelato', 'printcom', 'manual'],
    copy: copy(
      {
        label: 'Kalenders',
        title: 'Kalender maken met eigen foto’s',
        subtitle: 'Wandkalenders en bureaukalenders met foto’s, planning of merkuitstraling.',
        metaTitle: 'Kalender maken | Printen.eu',
        metaDescription: 'Maak een kalender met eigen foto’s of ontwerp. Bestel wandkalenders en bureaukalenders online.',
      },
      {
        label: 'Kalender',
        title: 'Kalender mit eigenen Fotos erstellen',
        subtitle: 'Wandkalender und Tischkalender mit Fotos, Planung oder Markenauftritt.',
        metaTitle: 'Kalender erstellen | Printen.eu',
        metaDescription: 'Kalender mit eigenen Fotos oder Design erstellen. Wand- und Tischkalender online bestellen.',
      },
      {
        label: 'Calendars',
        title: 'Create calendars with your own photos',
        subtitle: 'Wall calendars and desk calendars with photos, planning or branding.',
        metaTitle: 'Create custom calendars | Printen.eu',
        metaDescription: 'Create calendars with your own photos or design. Order wall and desk calendars online.',
      },
      {
        label: 'Calendriers',
        title: 'Créer un calendrier avec vos photos',
        subtitle: 'Calendriers muraux et de bureau avec photos, planning ou image de marque.',
        metaTitle: 'Calendrier personnalise | Printen.eu',
        metaDescription: 'Créez un calendrier avec vos photos ou design. Commandez calendriers muraux et de bureau.',
      },
    ),
  },
  {
    slug: 'kleding',
    aliases: ['apparel', 't-shirts', 'hoodies', 'sweatshirts', 'clothing-textile'],
    group: 'textile',
    providers: ['gelato', 'printful', 'manual'],
    copy: copy(
      {
        label: 'Kleding',
        title: 'Kleding bedrukken',
        subtitle: 'Shirts, sweaters, hoodies en textiel met logo, foto of ontwerp.',
        metaTitle: 'Kleding bedrukken | Printen.eu',
        metaDescription: 'Laat kleding bedrukken met logo, foto of ontwerp. Shirts, hoodies en sweaters online bestellen.',
      },
      {
        label: 'Bekleidung',
        title: 'Bekleidung bedrucken',
        subtitle: 'Shirts, Sweater, Hoodies und Textilien mit Logo, Foto oder Design.',
        metaTitle: 'Bekleidung bedrucken | Printen.eu',
        metaDescription: 'Bekleidung mit Logo, Foto oder Design bedrucken lassen. Shirts, Hoodies und Sweater bestellen.',
      },
      {
        label: 'Apparel',
        title: 'Custom printed apparel',
        subtitle: 'Shirts, sweaters, hoodies and textiles with a logo, photo or design.',
        metaTitle: 'Custom apparel printing | Printen.eu',
        metaDescription: 'Order custom printed apparel with logo, photo or design. Shirts, hoodies and sweaters online.',
      },
      {
        label: 'Vetements',
        title: 'Vetements personnalises',
        subtitle: 'T-shirts, sweats, hoodies et textiles avec logo, photo ou design.',
        metaTitle: 'Vetements personnalises | Printen.eu',
        metaDescription: 'Commandez des vêtements personnalisés avec logo, photo ou design. T-shirts, hoodies et sweats.',
      },
    ),
  },
  {
    slug: 'notitieboeken',
    aliases: ['notebooks'],
    parentSlug: 'paper-print',
    group: 'print',
    providers: ['gelato', 'manual'],
    copy: copy(
      {
        label: 'Notitieboeken',
        title: 'Notitieboek bedrukken',
        subtitle: 'Notitieboeken en schriften met eigen cover, logo of ontwerp.',
        metaTitle: 'Notitieboek bedrukken | Printen.eu',
        metaDescription: 'Laat notitieboeken bedrukken met eigen cover, logo of ontwerp. Online bestellen bij Printen.eu.',
      },
      {
        label: 'Notizbuecher',
        title: 'Notizbuch bedrucken',
        subtitle: 'Notizbücher und Hefte mit eigenem Cover, Logo oder Design.',
        metaTitle: 'Notizbuch bedrucken | Printen.eu',
        metaDescription: 'Notizbücher mit eigenem Cover, Logo oder Design bedrucken lassen.',
      },
      {
        label: 'Notebooks',
        title: 'Custom printed notebooks',
        subtitle: 'Notebooks with your own cover, logo or design.',
        metaTitle: 'Custom printed notebooks | Printen.eu',
        metaDescription: 'Order custom printed notebooks with your own cover, logo or design.',
      },
      {
        label: 'Carnets',
        title: 'Carnets personnalises',
        subtitle: 'Carnets avec votre couverture, logo ou design.',
        metaTitle: 'Carnets personnalises | Printen.eu',
        metaDescription: 'Commandez des carnets personnalisés avec votre couverture, logo ou design.',
      },
    ),
  },
  {
    slug: 'posters',
    aliases: ['poster', 'affiches'],
    parentSlug: 'paper-print',
    group: 'print',
    providers: ['printcom', 'gelato', 'manual'],
    copy: copy(
      {
        label: 'Posters',
        title: 'Posters laten drukken',
        subtitle: 'Alle posterproducten onder drukwerk: standaard posters, stoepbordposters, watervaste posters, zelfklevende posters en speciale posterafwerkingen.',
        metaTitle: 'Posters laten drukken | Drukwerk | Printen.eu',
        metaDescription: 'Bestel posters online bij Printen.eu. Kies uit standaard posters, stoepbordposters, watervaste posters, zelfklevende posters en speciale afwerkingen.',
      },
      {
        label: 'Poster',
        title: 'Poster drucken lassen',
        subtitle: 'Alle Posterprodukte unter Drucksachen: Standardposter, Kundenstopper-Poster, wasserfeste Poster, selbstklebende Poster und besondere Veredelungen.',
        metaTitle: 'Poster drucken lassen | Drucksachen | Printen.eu',
        metaDescription: 'Poster online bei Printen.eu bestellen. Wählen Sie Standardposter, Kundenstopper-Poster, wasserfeste Poster, selbstklebende Poster und Veredelungen.',
      },
      {
        label: 'Posters',
        title: 'Custom poster printing',
        subtitle: 'All poster products under print: standard posters, pavement sign posters, waterproof posters, self-adhesive posters and special poster finishes.',
        metaTitle: 'Custom poster printing | Print products | Printen.eu',
        metaDescription: 'Order posters online at Printen.eu. Choose standard posters, pavement sign posters, waterproof posters, self-adhesive posters and special finishes.',
      },
      {
        label: 'Posters',
        title: 'Impression de posters',
        subtitle: 'Tous les produits poster dans les imprimés: posters standard, posters pour stop-trottoirs, posters résistants à l’eau, posters adhésifs et finitions speciales.',
        metaTitle: 'Impression de posters | Imprimes | Printen.eu',
        metaDescription: 'Commandez des posters en ligne chez Printen.eu. Choisissez posters standard, posters pour stop-trottoirs, posters résistants à l’eau, posters adhésifs et finitions.',
      },
    ),
  },
  {
    slug: 'fotoprints',
    aliases: ['posters-fotoprints', 'photo-poster', 'photo-posters', 'foto-op-poster', 'fotoprint', 'fotoprints'],
    parentSlug: 'wanddecoratie',
    group: 'photo-wall',
    providers: ['printcom', 'gelato', 'prodigi', 'manual'],
    copy: basicCopy(
      'Fotoprints',
      'Fotoprints bestellen',
      'Foto op poster, kunstprints en losse fotoprints voor wand, interieur en persoonlijke decoratie.',
      'Fotoprints bestellen | Printen.eu',
      'Bestel fotoprints met eigen foto, kunstwerk of ontwerp. Geschikt voor thuis, kantoor en wanddecoratie.',
    ),
  },
  {
    slug: 'ingelijste-prints',
    aliases: ['framed-posters', 'framed-prints', 'frames', 'fotolijst', 'ingelijste-poster', 'mounted-framed-posters'],
    parentSlug: 'wanddecoratie',
    group: 'photo-wall',
    providers: ['printcom', 'gelato', 'prodigi', 'manual'],
    copy: basicCopy(
      'Ingelijste prints',
      'Ingelijste posters en fotolijsten',
      'Ingelijste posters, fotolijsten, multi-fotolijsten en wandprints met lijst.',
      'Ingelijste prints bestellen | Printen.eu',
      'Bestel ingelijste prints, posters in lijst en fotolijsten met eigen foto of ontwerp.',
    ),
  },
  {
    slug: 'plexiglas-acryl',
    aliases: ['acrylic', 'acrylglas', 'plexiglas', 'acrylglas-print', 'acrylic-photo-prints'],
    parentSlug: 'wanddecoratie',
    group: 'photo-wall',
    providers: ['printcom', 'gelato', 'prodigi', 'manual'],
    copy: basicCopy(
      'Plexiglas & acryl',
      'Foto op plexiglas en acryl',
      'Glanzende foto- en designprints op plexiglas of acrylglas voor moderne wanddecoratie.',
      'Foto op plexiglas bestellen | Printen.eu',
      'Bestel foto op plexiglas of acrylglas met eigen beeld, kunstwerk of bedrijfsontwerp.',
    ),
  },
  {
    slug: 'aluminium-dibond',
    aliases: ['aluminium-print', 'dibond-print', 'metallic', 'metal-prints', 'aluminium', 'dibond'],
    parentSlug: 'wanddecoratie',
    group: 'photo-wall',
    providers: ['printcom', 'gelato', 'prodigi', 'manual'],
    copy: copy(
      {
        label: 'Aluminium fotopanelen',
        title: 'Aluminium fotopanelen bedrukken',
        subtitle: 'Laat een foto op aluminium of dibond drukken voor een strak fotopaneel aan de wand.',
        metaTitle: 'Aluminium fotopanelen bedrukken | Printen.eu',
        metaDescription: 'Laat aluminium fotopanelen bedrukken met je eigen foto, kunstwerk of ontwerp. Kies dibond, aluminium of geborsteld aluminium en bestel vanaf 1 stuk.',
        seoHeading: 'Aluminium fotopanelen bedrukken',
        seoIntro: 'Een aluminium fotopaneel geeft je foto, kunstwerk of zakelijke ontwerp een strakke en moderne uitstraling. Je kiest een paneel op aluminium, dibond of geborsteld aluminium, uploadt je bestand en bepaalt daarna formaat, afwerking en ophanging. Dat maakt aluminium fotopanelen geschikt voor woonkamer, kantoor, winkel, galerie, beurswand of entree.',
        seoSections: [
          {
            heading: 'Aluminium fotopanelen bedrukken',
            body: 'Een aluminium fotopaneel geeft je foto, kunstwerk of zakelijke ontwerp een strakke en moderne uitstraling. Je kiest een paneel op aluminium, dibond of geborsteld aluminium, uploadt je bestand en bepaalt daarna formaat, afwerking en ophanging. Dat maakt aluminium fotopanelen geschikt voor woonkamer, kantoor, winkel, galerie, beurswand of entree. Kies dibond wanneer je een stevig, vlak en licht paneel zoekt met een professionele afwerking. Kies geborsteld aluminium voor een subtiele metallic look, vooral bij zwart-witfoto\'s, architectuur, typografie en ontwerpen met veel contrast. Controleer voor bestellen de beeldverhouding, resolutie, kijkafstand en of je een ophangsysteem of boorgaten nodig hebt.',
            links: [
              { label: 'Dibond print bekijken', productSlug: 'dibond-print' },
              { label: 'Fotopaneel met baklijst', productSlug: 'fotopaneel-met-baklijst' },
              { label: 'Aluminium print', productSlug: 'aluminium-print' },
            ],
          },
          {
            heading: 'Dibond, aluminium of geborsteld aluminium kiezen',
            body: 'De materiaalkeuze bepaalt vooral de uitstraling en de praktische toepassing. Dibond is een aluminium composietplaat met een vlakke, stevige basis en is geschikt voor scherpe foto\'s, zakelijke prints, interieurpanelen en presentaties. Een aluminium print is logisch wanneer je een moderne, strakke wandprint zoekt die licht oogt en goed past in kantoor, winkel of woonruimte. Geborsteld aluminium geeft delen van het beeld een subtiele metaalstructuur; dat werkt vooral goed bij contrast, zwart-wit, typografie, architectuur en minimalistische ontwerpen. Wil je vooral kleurkracht en een neutrale ondergrond, vergelijk dan ook acrylglas of andere wanddecoratie. Wil je een paneel dat sterker richting signing gaat, vergelijk dan dibond binnen plaatmateriaal.',
            links: [
              { label: 'Foto op aluminium of dibond', category: 'aluminium-dibond' },
              { label: 'Dibond platen vergelijken', category: 'plaat-dibond' },
              { label: 'Alle wanddecoratie', category: 'wanddecoratie' },
            ],
          },
          {
            heading: 'Formaat, afwerking en ophanging bepalen',
            body: 'Begin bij de plek waar het fotopaneel komt te hangen. Een klein paneel wordt meestal van dichtbij bekeken en vraagt om fijne details en een rustige uitsnede. Een groot aluminium fotopaneel moet ook vanaf afstand duidelijk blijven, dus let op compositie, contrast en de verhouding tussen onderwerp en achtergrond. Controleer of je foto genoeg resolutie heeft voor het gekozen formaat en voorkom dat belangrijke onderdelen te dicht bij de rand staan. Daarna kies je de afwerking die past bij de ruimte: strak en vlak voor een moderne wand, met baklijst voor meer diepte of met ophangsysteem wanneer je het paneel zonder zichtbare schroeven wilt monteren. Voor kantoor, winkel en beurswand is herhaalbaarheid vaak belangrijk; kies dan materiaal, formaat en ophanging die later opnieuw te bestellen zijn.',
            links: [
              { label: 'Ophangplaat voor panelen', productSlug: 'ophangplaat' },
              { label: 'Baklijst voor plaatmateriaal', productSlug: 'baklijst-voor-plaatmateriaal' },
            ],
          },
          {
            heading: 'Foto of ontwerp voorbereiden',
            body: 'Een aluminium fotopaneel werkt het best met een scherp origineel bestand. Gebruik bij voorkeur een foto of ontwerp met voldoende pixels, goede belichting en duidelijke contrasten. Kijk vooraf of gezichten, logo\'s, tekst en belangrijke lijnen niet tegen de rand vallen. Voor een persoonlijke foto is sfeer vaak belangrijker dan veel tekst; voor een zakelijke print telt leesbaarheid en merkherkenning zwaarder. Bij geborsteld aluminium kan wit of heel licht beeld anders overkomen door het metallic effect, terwijl dibond en standaard aluminium meestal neutraler ogen. Twijfel je tussen een zachte fotoprint en een strakke plaat, vergelijk dan ook foto op poster of acrylglas voordat je bestelt.',
            links: [
              { label: 'Strak fotopaneel op aluminium', category: 'aluminium-dibond' },
              { label: 'Foto op poster vergelijken', productSlug: 'foto-op-poster' },
              { label: 'Acrylglas print', productSlug: 'acrylglas-print' },
            ],
          },
          {
            heading: 'Toepassingen voor thuis en zakelijk',
            body: 'Aluminium fotopanelen worden vaak gekozen voor een rustige, premium wandpresentatie. Thuis past een paneel goed boven bank, eettafel, bureau of in een hal waar een poster te kwetsbaar voelt. Voor bedrijven is aluminium geschikt voor kantoorinrichting, ontvangstruimtes, winkelpresentaties, galeriebeelden, beurswanden, portfolio\'s, wayfinding en decoratieve merkbeelden. Omdat het paneel stevig en vlak is, oogt het professioneler dan een losse print op papier. Kies voor een enkele blikvanger wanneer het beeld centraal staat, of combineer meerdere panelen wanneer je een serie foto\'s, productbeelden of locaties wilt tonen. Bekijk op de productpagina altijd de actuele materialen, formaten en accessoires voordat je de bestelling afrondt.',
            links: [
              { label: 'Aluminium fotopanelen', category: 'aluminium-dibond' },
              { label: 'Plaatmateriaal bedrukken', category: 'plaatmateriaal' },
            ],
          },
        ],
      },
      {
        label: 'Aluminium & dibond',
        title: 'Foto op aluminium en dibond',
        subtitle: 'Strakke wandprints op aluminium, geborsteld aluminium en dibond voor interieur en presentatie.',
        metaTitle: 'Foto op aluminium en dibond | Printen.eu',
        metaDescription: 'Bestel foto op aluminium, geborsteld aluminium of dibond met eigen foto of ontwerp.',
      },
      {
        label: 'Aluminium & dibond',
        title: 'Foto op aluminium en dibond',
        subtitle: 'Strakke wandprints op aluminium, geborsteld aluminium en dibond voor interieur en presentatie.',
        metaTitle: 'Foto op aluminium en dibond | Printen.eu',
        metaDescription: 'Bestel foto op aluminium, geborsteld aluminium of dibond met eigen foto of ontwerp.',
      },
      {
        label: 'Aluminium & dibond',
        title: 'Foto op aluminium en dibond',
        subtitle: 'Strakke wandprints op aluminium, geborsteld aluminium en dibond voor interieur en presentatie.',
        metaTitle: 'Foto op aluminium en dibond | Printen.eu',
        metaDescription: 'Bestel foto op aluminium, geborsteld aluminium of dibond met eigen foto of ontwerp.',
      },
    ),
  },
  {
    slug: 'fototegels',
    aliases: ['fototegel', 'photo-tiles', 'keramische-tegels', 'ceramische-tegels', 'tegels'],
    parentSlug: 'wanddecoratie',
    group: 'photo-wall',
    providers: ['printcom', 'prodigi', 'manual'],
    copy: basicCopy(
      'Fototegels',
      'Fototegels en keramische tegels',
      'Fototegels en keramische tegels met eigen foto, patroon of illustratie.',
      'Fototegels bestellen | Printen.eu',
      'Bestel fototegels en keramische tegels met eigen foto, patroon of ontwerp.',
    ),
  },
  {
    slug: 'akoestiek',
    aliases: ['acoustic', 'akoestisch', 'akoestische-panelen', 'acousticpro-panelen', 'akoestische-hexagon-panelen', 'acousticpro-hexagons'],
    parentSlug: 'indoor-interior',
    group: 'interior',
    providers: ['probo', 'manual'],
    copy: acousticCategoryCopy,
  },
  {
    slug: 'behang-wandbekleding',
    aliases: ['behang', 'wallpaper', 'fotobehang', 'behangrol', 'behangcirkel', 'wall-covering'],
    parentSlug: 'indoor-interior',
    group: 'interior',
    providers: ['printcom', 'probo', 'gelato', 'manual'],
    copy: basicCopy(
      'Behang & wandbekleding',
      'Behang en wandbekleding op maat',
      'Fotobehang, behangrollen, behangcirkels en wandbekleding met eigen foto of patroon.',
      'Behang en wandbekleding op maat | Printen.eu',
      'Bestel fotobehang, behangrollen en wandbekleding met eigen foto, patroon of ontwerp.',
    ),
  },
  {
    slug: 'deurmatten',
    aliases: ['deurmat', 'deurmatten', 'doormats', 'deurmat-met-rand'],
    parentSlug: 'floor-decoration',
    group: 'interior',
    providers: ['probo', 'manual'],
    copy: basicCopy(
      'Deurmatten',
      'Deurmatten bedrukken',
      'Bedrukte deurmatten voor entree, winkel, kantoor en woning.',
      'Deurmat bedrukken | Printen.eu',
      'Bestel een deurmat met eigen logo, foto, tekst of patroon voor binnen en entree.',
    ),
  },
  {
    slug: 'vloerbescherming',
    aliases: ['vloerbeschermer', 'floor-protection', 'floor-protectors'],
    parentSlug: 'floor-decoration',
    group: 'interior',
    providers: ['probo', 'manual'],
    copy: basicCopy(
      'Vloerbescherming',
      'Vloerbescherming met print',
      'Vloerbeschermers en functionele vloerproducten met eigen ontwerp.',
      'Vloerbescherming met print | Printen.eu',
      'Bestel vloerbescherming en vloerproducten met eigen ontwerp voor interieur en werkplek.',
    ),
  },
  {
    slug: 'vinylstickers',
    aliases: ['vinylsticker', 'vinyl-stickers', 'grote-stickers', 'outdoor-stickers'],
    parentSlug: 'stickers-tapes',
    group: 'stickers',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Vinylstickers',
      'Vinylstickers laten drukken',
      'Sterke vinylstickers voor binnen, buiten, raam, voertuig en promotie.',
      'Vinylstickers laten drukken | Printen.eu',
      'Bestel vinylstickers met eigen ontwerp voor binnen, buiten, raam, voertuig en reclame.',
    ),
  },
  {
    slug: 'muurstickers',
    aliases: ['muursticker', 'wall-stickers', 'wall-decals'],
    parentSlug: 'stickers-tapes',
    group: 'stickers',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Muurstickers',
      'Muurstickers met eigen ontwerp',
      'Muurstickers, wandteksten en decoratieve stickers voor interieur en retail.',
      'Muurstickers bestellen | Printen.eu',
      'Bestel muurstickers en wandstickers met eigen foto, tekst, logo of ontwerp.',
    ),
  },
  {
    slug: 'labels-etiketten',
    aliases: ['labels', 'etiketten', 'stickers-op-rol', 'stickervellen', 'label-sheets'],
    parentSlug: 'stickers-tapes',
    group: 'stickers',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Labels & etiketten',
      'Labels en etiketten bedrukken',
      'Labels, etiketten, stickervellen en stickers op rol voor verpakking en merk.',
      'Labels en etiketten bedrukken | Printen.eu',
      'Bestel labels, etiketten en stickervellen met eigen logo of ontwerp.',
    ),
  },
  {
    slug: 'spandoeken-banners',
    aliases: ['spandoek', 'spandoeken', 'banners', 'outdoor-banners', 'gevelbanner'],
    parentSlug: 'outdoor-advertising',
    group: 'outdoor',
    providers: ['printcom', 'probo', 'prodigi', 'manual'],
    copy: basicCopy(
      'Spandoeken & banners',
      'Spandoeken en banners bedrukken',
      'Spandoeken, gevelbanners en outdoor banners voor reclame, event en bouw.',
      'Spandoeken en banners bedrukken | Printen.eu',
      'Bestel spandoeken en banners met eigen ontwerp voor buitenreclame, event of gevel.',
    ),
  },
  {
    slug: 'stoepborden',
    aliases: ['stoepbord', 'stoepborden', 'pavement-signs', 'a-bord', 'premium-stoepbord', 'stoepbord-met-watertank'],
    parentSlug: 'displays-pos',
    group: 'display',
    providers: ['probo', 'printcom', 'manual'],
    copy: pavementSignCopy,
  },
  {
    slug: 'lichtreclame',
    aliases: ['lichtbak', 'lichtbakken', 'lightboxes', 'light-boxes', 'led-signs'],
    parentSlug: 'outdoor-advertising',
    group: 'outdoor',
    providers: ['probo', 'manual'],
    copy: basicCopy(
      'Lichtreclame',
      'Lichtbakken en lichtreclame',
      'Lichtbakken en verlichte signing voor gevel, winkel en zichtbaarheid.',
      'Lichtbakken en lichtreclame | Printen.eu',
      'Bestel lichtbakken en lichtreclame met eigen ontwerp voor gevel, winkel of balie.',
    ),
  },
  {
    slug: 'bewegwijzering-borden',
    aliases: ['bewegwijzeringsbord', 'bewegwijzering', 'reclamebord', 'reclameborden', 'signage-boards'],
    parentSlug: 'outdoor-advertising',
    group: 'outdoor',
    providers: ['probo', 'printcom', 'manual'],
    copy: basicCopy(
      'Borden & bewegwijzering',
      'Borden en bewegwijzering bedrukken',
      'Reclameborden, bewegwijzering en signborden voor binnen en buiten.',
      'Borden en bewegwijzering | Printen.eu',
      'Bestel reclameborden, bewegwijzering en signborden met eigen ontwerp.',
    ),
  },
  {
    slug: 'naamborden-huisnummers',
    aliases: ['naambord', 'naamborden', 'huisnummerbord', 'huisnummers', 'door-nameplate', 'front-door-sign'],
    parentSlug: 'bewegwijzering-borden',
    group: 'outdoor',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Naamborden & huisnummers',
      'Naambord of huisnummerbord bestellen',
      'Naamborden, huisnummerborden en entreebordjes voor voordeur, gevel en praktijkruimte.',
      'Naambord voordeur bestellen | Printen.eu',
      'Bestel een naambord of huisnummerbord voor voordeur en gevel met eigen tekst, materiaal en ontwerp.',
    ),
  },
  {
    slug: 'tuinposters',
    aliases: ['tuinposter', 'garden-poster', 'garden-posters', 'outdoor-living'],
    parentSlug: 'outdoor-advertising',
    group: 'outdoor',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Tuinposters',
      'Tuinposter met eigen foto',
      'Tuinposters en buitenprints voor tuin, balkon, terras en schutting.',
      'Tuinposter met eigen foto | Printen.eu',
      'Bestel een tuinposter of buitenprint met eigen foto, patroon of ontwerp.',
    ),
  },
  {
    slug: 'roll-up-banners',
    aliases: ['roll-up-banner', 'rollup-banner', 'roll-ups', 'retractable-banners'],
    parentSlug: 'beurs-evenement',
    group: 'exhibition',
    providers: ['printcom', 'probo', 'gelato', 'manual'],
    copy: basicCopy(
      'Roll-up banners',
      'Roll-up banners bedrukken',
      'Roll-up banners voor beurs, winkel, presentatie, showroom en event.',
      'Roll-up banners bedrukken | Printen.eu',
      'Bestel roll-up banners met eigen ontwerp voor beurs, presentatie, winkel of event.',
    ),
  },
  {
    slug: 'beurswanden',
    aliases: ['beurswand', 'beurswanden', 'exhibition-walls', 'stand-walls', 'vrijstaand-textielframe'],
    parentSlug: 'beurs-evenement',
    group: 'exhibition',
    providers: ['probo', 'printcom', 'manual'],
    copy: basicCopy(
      'Beurswanden',
      'Beurswanden en presentatiewanden',
      'Beurswanden, standwanden en presentatiewanden met verwisselbare print.',
      'Beurswanden bestellen | Printen.eu',
      'Bestel beurswanden en presentatiewanden met eigen ontwerp voor beurs en event.',
    ),
  },
  {
    slug: 'festival-eventdoeken',
    aliases: ['festivaldoeken', 'eventdoeken', 'event-banners', 'zipper-banners'],
    parentSlug: 'beurs-evenement',
    group: 'exhibition',
    providers: ['probo', 'printcom', 'manual'],
    copy: basicCopy(
      'Festival- & eventdoeken',
      'Festivaldoeken en eventprints',
      'Festivaldoeken, eventbanners en aankleding voor tijdelijke campagnes en events.',
      'Festivaldoeken en eventprints | Printen.eu',
      'Bestel festivaldoeken en eventprints met eigen ontwerp voor evenement of actie.',
    ),
  },
  {
    slug: 'beachflags',
    aliases: ['beachflag', 'beachflags', 'beach-flags'],
    parentSlug: 'vlaggen',
    group: 'flags',
    providers: ['probo', 'printcom', 'manual'],
    copy: basicCopy(
      'Beachflags',
      'Beachflags bedrukken',
      'Beachflags en promotievlaggen voor winkel, event, sportclub en beurs.',
      'Beachflags bedrukken | Printen.eu',
      'Bestel beachflags met eigen logo of ontwerp voor buitenreclame en event.',
    ),
  },
  {
    slug: 'gevelvlaggen',
    aliases: ['gevelvlag', 'gevelvlaggen', 'facade-flags', 'kioskvlaggen'],
    parentSlug: 'vlaggen',
    group: 'flags',
    providers: ['probo', 'printcom', 'manual'],
    copy: basicCopy(
      'Gevelvlaggen',
      'Gevelvlaggen bedrukken',
      'Gevelvlaggen, kioskvlaggen en banieren voor winkels en panden.',
      'Gevelvlaggen bedrukken | Printen.eu',
      'Bestel gevelvlaggen en banieren met eigen ontwerp voor winkel of bedrijfspand.',
    ),
  },
  {
    slug: 'mastvlaggen',
    aliases: ['mastvlag', 'mastvlaggen', 'pole-flags', 'baniervlaggen'],
    parentSlug: 'vlaggen',
    group: 'flags',
    providers: ['probo', 'printcom', 'manual'],
    copy: basicCopy(
      'Mastvlaggen',
      'Mastvlaggen bedrukken',
      'Mastvlaggen, baniervlaggen en vlaggen voor terrein, gevel en event.',
      'Mastvlaggen bedrukken | Printen.eu',
      'Bestel mastvlaggen en baniervlaggen met eigen logo, ontwerp of campagnebeeld.',
    ),
  },
  {
    slug: 'plaat-forex',
    aliases: ['forex', 'forex-print', 'pvc-signs', 'foam-print-product'],
    parentSlug: 'plaatmateriaal',
    group: 'materials',
    providers: ['printcom', 'probo', 'gelato', 'manual'],
    copy: basicCopy(
      'Forex & PVC platen',
      'Forex en PVC platen bedrukken',
      'Lichte kunststof platen voor signing, presentatie, interieur en tijdelijke borden.',
      'Forex en PVC platen bedrukken | Printen.eu',
      'Bestel forex platen en PVC signmateriaal met eigen ontwerp of foto.',
    ),
  },
  {
    slug: 'plaat-dibond',
    aliases: ['dibond', 'aluminium-composite', 'alu-dibond'],
    parentSlug: 'plaatmateriaal',
    group: 'materials',
    providers: ['printcom', 'probo', 'prodigi', 'manual'],
    copy: basicCopy(
      'Dibond platen',
      'Dibond platen bedrukken',
      'Stevige aluminium composietplaten voor reclame, wanddecoratie en buitengebruik.',
      'Dibond platen bedrukken | Printen.eu',
      'Bestel dibond platen met eigen print voor reclamebord, wandpaneel of presentatie.',
    ),
  },
  {
    slug: 'plaat-karton-board',
    aliases: ['display-board', 'foamboard', 'displaykarton', 'corrugated-board', 're-board'],
    parentSlug: 'plaatmateriaal',
    group: 'materials',
    providers: ['printcom', 'probo', 'prodigi', 'manual'],
    copy: basicCopy(
      'Displaykarton & boards',
      'Displaykarton en boards bedrukken',
      'Displaykarton, foamboard, kanaalplaat en lichte boards voor presentatie en POS.',
      'Displaykarton en boards | Printen.eu',
      'Bestel displaykarton, foamboard en lichte boards met eigen ontwerp.',
    ),
  },
  {
    slug: 'flyers',
    aliases: ['flyer', 'flyers'],
    parentSlug: 'paper-print',
    group: 'print',
    providers: ['printcom', 'gelato', 'manual'],
    copy: basicCopy(
      'Flyers',
      'Flyers drukken',
      'Flyers voor acties, events, horeca, winkels en zakelijke promotie.',
      'Flyers drukken | Printen.eu',
      'Bestel flyers met eigen ontwerp in verschillende formaten, oplages en afwerkingen.',
    ),
  },
  {
    slug: 'folders-brochures',
    aliases: ['folder', 'folders', 'brochure', 'brochures', 'gevouwen-folder', 'harmonicafolder', 'wikkelfolder', 'single-fold-brochures', 'multipage-brochures'],
    parentSlug: 'paper-print',
    group: 'print',
    providers: ['printcom', 'gelato', 'manual'],
    copy: basicCopy(
      'Folders & brochures',
      'Folders en brochures drukken',
      'Gevouwen folders, brochures, harmonicafolders en wikkelfolders voor uitleg en verkoop.',
      'Folders en brochures drukken | Printen.eu',
      'Bestel folders en brochures met eigen ontwerp voor bedrijf, actie, menu of presentatie.',
    ),
  },
  {
    slug: 'visitekaartjes',
    aliases: ['business-cards', 'businesscards', 'visitekaartjes'],
    parentSlug: 'paper-print',
    group: 'print',
    providers: ['printcom', 'gelato', 'manual'],
    copy: basicCopy(
      'Visitekaartjes',
      'Visitekaartjes drukken',
      'Visitekaartjes en zakelijke kaartjes met eigen huisstijl, logo en afwerking.',
      'Visitekaartjes drukken | Printen.eu',
      'Visitekaartjes drukken met eigen logo of ontwerp. Kies papiersoort, formaat en luxe afwerking, zie direct je prijs en bestel online met gratis bestandscheck.',
    ),
  },
  {
    slug: 'kaarten',
    aliases: ['cards', 'wenskaarten', 'ansichtkaarten', 'kaartenset', 'folded-cards', 'pack-of-cards'],
    parentSlug: 'paper-print',
    group: 'print',
    providers: ['gelato', 'printcom', 'manual'],
    copy: basicCopy(
      'Kaarten',
      'Kaarten drukken',
      'Wenskaarten, ansichtkaarten, kaartensets en gevouwen kaarten met eigen ontwerp.',
      'Kaarten drukken | Printen.eu',
      'Bestel kaarten, wenskaarten en ansichtkaarten met eigen foto, tekst of ontwerp.',
    ),
  },
  {
    slug: 'briefpapier-enveloppen',
    aliases: ['briefpapier', 'letterheads', 'enveloppen', 'envelopes', 'blank-envelopes'],
    parentSlug: 'paper-print',
    group: 'print',
    providers: ['gelato', 'printcom', 'manual'],
    copy: basicCopy(
      'Briefpapier & enveloppen',
      'Briefpapier en enveloppen drukken',
      'Zakelijk briefpapier, enveloppen en correspondentiestijl met eigen huisstijl.',
      'Briefpapier en enveloppen | Printen.eu',
      'Bestel briefpapier en enveloppen met logo, adresgegevens en eigen huisstijl.',
    ),
  },
  {
    slug: 'bouwtekeningen',
    aliases: ['bouwtekening', 'bouwtekeningen', 'construction-drawings', 'plans'],
    parentSlug: 'paper-print',
    group: 'print',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Bouwtekeningen',
      'Bouwtekeningen printen',
      'Bouwtekeningen, technische tekeningen en plannen printen op groot formaat.',
      'Bouwtekeningen printen | Printen.eu',
      'Bestel bouwtekeningen en technische tekeningen online met eigen PDF-bestand.',
    ),
  },
  {
    slug: 'presentatiemappen',
    aliases: ['presentatiemap', 'presentation-folders'],
    parentSlug: 'paper-print',
    group: 'print',
    providers: ['gelato', 'printcom', 'manual'],
    copy: basicCopy(
      'Presentatiemappen',
      'Presentatiemappen drukken',
      'Presentatiemappen en zakelijke mappen voor offertes, documenten en salesmateriaal.',
      'Presentatiemappen drukken | Printen.eu',
      'Bestel presentatiemappen met eigen logo, huisstijl of ontwerp.',
    ),
  },
  {
    slug: 'drinkwaren',
    aliases: ['drinkware', 'bottles', 'waterfles', 'drinkfles'],
    parentSlug: 'relatiegeschenken',
    group: 'gifts',
    providers: ['printcom', 'gelato', 'printful', 'prodigi', 'manual'],
    copy: basicCopy(
      'Drinkwaren',
      'Mokken en drinkwaren bedrukken',
      'Mokken, drinkflessen en drinkwaren met foto, logo of ontwerp.',
      'Mokken en drinkwaren bedrukken | Printen.eu',
      'Bestel mokken, drinkflessen en drinkwaren met eigen foto, logo of ontwerp.',
    ),
  },
  {
    slug: 'kussens',
    aliases: ['sierkussen', 'buitenkussen', 'cushions', 'pillows'],
    parentSlug: 'home-living',
    group: 'gifts',
    providers: ['probo', 'prodigi', 'gelato', 'printful', 'manual'],
    copy: basicCopy(
      'Kussens',
      'Kussens bedrukken',
      'Sierkussens, buitenkussens en woonkussens met eigen foto of patroon.',
      'Kussens bedrukken | Printen.eu',
      'Bestel kussens met eigen foto, patroon, logo of ontwerp voor binnen en buiten.',
    ),
  },
  {
    slug: 'dekens-handdoeken',
    aliases: ['fleecedeken', 'handdoek-en-strandlaken', 'blankets', 'towels', 'beach-towels'],
    parentSlug: 'textiel-cadeaus',
    group: 'textile',
    providers: ['probo', 'prodigi', 'gelato', 'manual'],
    copy: basicCopy(
      'Dekens & handdoeken',
      'Dekens en handdoeken bedrukken',
      'Fleecedekens, handdoeken en strandlakens met eigen foto of ontwerp.',
      'Dekens en handdoeken bedrukken | Printen.eu',
      'Bestel dekens, handdoeken en strandlakens met eigen foto, patroon of logo.',
    ),
  },
  {
    slug: 'tassen',
    aliases: ['draagtas', 'tote-bags', 'tote', 'bags'],
    parentSlug: 'textiel-cadeaus',
    group: 'textile',
    providers: ['probo', 'gelato', 'printful', 'prodigi', 'manual'],
    copy: basicCopy(
      'Tassen',
      'Tassen bedrukken',
      'Draagtassen, tote bags en textieltassen met eigen ontwerp of logo.',
      'Tassen bedrukken | Printen.eu',
      'Bestel tassen en tote bags met eigen logo, foto of ontwerp.',
    ),
  },
  {
    slug: 'foto-cadeaus',
    aliases: ['foto-hanger', 'photo-gifts', 'zitzak', 'pendants'],
    parentSlug: 'textiel-cadeaus',
    group: 'gifts',
    providers: ['prodigi', 'probo', 'manual'],
    copy: basicCopy(
      'Fotocadeaus',
      'Fotocadeaus personaliseren',
      'Persoonlijke fotocadeaus, hangers en cadeauproducten met eigen foto of ontwerp.',
      'Fotocadeaus personaliseren | Printen.eu',
      'Bestel fotocadeaus en persoonlijke cadeauproducten met eigen foto of ontwerp.',
    ),
  },
  {
    slug: 'shirts-tops',
    aliases: ['t-shirt-unisex', 't-shirts', 'tanktop', 'tank-tops', 'shirts'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['gelato', 'printful', 'manual'],
    copy: basicCopy(
      'Shirts & tops',
      'Shirts en tops bedrukken',
      'T-shirts, tanktops en tops met eigen logo, foto of ontwerp.',
      'Shirts en tops bedrukken | Printen.eu',
      'Bestel shirts en tops met eigen logo, tekst, foto of ontwerp.',
    ),
  },
  {
    slug: 'dames-kleding',
    aliases: ['damesmode', 'womens-apparel', 'women-clothing', 'women'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['printful', 'gelato', 'manual'],
    copy: basicCopy(
      'Dames kleding',
      'Dames kleding bedrukken',
      'Dames shirts, tops, hoodies, sportkleding en all-over kleding met eigen ontwerp.',
      'Dames kleding bedrukken | Printen.eu',
      'Bestel dames kleding met eigen print, logo, tekst, patroon of all-over ontwerp.',
    ),
  },
  {
    slug: 'heren-kleding',
    aliases: ['herenmode', 'mens-apparel', 'men-clothing', 'men'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['printful', 'gelato', 'manual'],
    copy: basicCopy(
      'Heren kleding',
      'Heren kleding bedrukken',
      'Heren shirts, hoodies, sportkleding, shorts en all-over kleding met eigen ontwerp.',
      'Heren kleding bedrukken | Printen.eu',
      'Bestel heren kleding met eigen print, logo, tekst, patroon of all-over ontwerp.',
    ),
  },
  {
    slug: 'unisex-kleding',
    aliases: ['unisex-apparel', 'unisex-clothing'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['printful', 'gelato', 'manual'],
    copy: basicCopy(
      'Unisex kleding',
      'Unisex kleding bedrukken',
      'Unisex shirts, hoodies, sweaters en teamkleding met eigen logo of ontwerp.',
      'Unisex kleding bedrukken | Printen.eu',
      'Bestel unisex kleding met eigen print, logo, tekst, patroon of artwork.',
    ),
  },
  {
    slug: 'sportkleding',
    aliases: ['sportswear', 'sport-apparel', 'athletic-apparel', 'yoga-kleding'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['printful', 'gelato', 'manual'],
    copy: basicCopy(
      'Sportkleding',
      'Sportkleding bedrukken',
      'Sportshirts, jerseys, rashguards, leggings en teamkleding met eigen ontwerp.',
      'Sportkleding bedrukken | Printen.eu',
      'Bestel sportkleding met eigen logo, teamnaam, patroon of all-over print.',
    ),
  },
  {
    slug: 'zwemkleding',
    aliases: ['swimwear', 'badkleding', 'bikini', 'rashguards'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['printful', 'gelato', 'manual'],
    copy: basicCopy(
      'Zwemkleding',
      'Zwemkleding bedrukken',
      'Bikini’s, badpakken, rashguards en zwemshorts met eigen all-over ontwerp.',
      'Zwemkleding bedrukken | Printen.eu',
      'Bestel zwemkleding met eigen print, patroon, logo of all-over artwork.',
    ),
  },
  {
    slug: 'leggings-shorts',
    aliases: ['leggings', 'shorts', 'joggers', 'broeken'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['printful', 'gelato', 'manual'],
    copy: basicCopy(
      'Leggings & shorts',
      'Leggings en shorts bedrukken',
      'Leggings, shorts, joggers en sportbroeken met eigen print of patroon.',
      'Leggings en shorts bedrukken | Printen.eu',
      'Bestel leggings, shorts en joggers met eigen logo, patroon of all-over print.',
    ),
  },
  {
    slug: 'all-over-kleding',
    aliases: ['all-over-apparel', 'cut-sew-apparel', 'aop-kleding'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['printful', 'manual'],
    copy: basicCopy(
      'All-over kleding',
      'All-over kleding bedrukken',
      'Kleding met full-bleed patronen, panelen en all-over artwork.',
      'All-over kleding bedrukken | Printen.eu',
      'Bestel all-over kleding met eigen patroon, artwork of full-bleed print.',
    ),
  },
  {
    slug: 'hoodies-sweaters',
    aliases: ['hoodie-unisex', 'hoodies', 'sweater', 'sweatshirts'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['gelato', 'printful', 'manual'],
    copy: basicCopy(
      'Hoodies & sweaters',
      'Hoodies en sweaters bedrukken',
      'Hoodies, sweaters en warme kleding met eigen logo of ontwerp.',
      'Hoodies en sweaters bedrukken | Printen.eu',
      'Bestel hoodies en sweaters met eigen logo, tekst of ontwerp.',
    ),
  },
  {
    slug: 'kinder-baby-kleding',
    aliases: ['kinderkleding', 'babykleding', 'kids-apparel', 'baby-clothing'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['gelato', 'printful', 'manual'],
    copy: basicCopy(
      'Kinder- & babykleding',
      'Kinderkleding en babykleding bedrukken',
      'Kinderkleding, babykleding en kleine maten met eigen ontwerp.',
      'Kinderkleding en babykleding | Printen.eu',
      'Bestel kinder- en babykleding met eigen print, tekst of ontwerp.',
    ),
  },
  {
    slug: 'werkkleding-horeca',
    aliases: ['polo', 'polos', 'schort', 'aprons', 'workwear'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['gelato', 'printful', 'manual'],
    copy: basicCopy(
      'Werkkleding & horeca',
      'Werkkleding en horeca textiel bedrukken',
      'Polo’s, schorten en praktische kleding met logo of huisstijl.',
      'Werkkleding en horeca textiel | Printen.eu',
      'Bestel polo’s, schorten en werkkleding met eigen logo of ontwerp.',
    ),
  },
  {
    slug: 'caps-mutsen',
    aliases: ['dad-cap', 'snapback', 'truckerspet', 'vissershoed', 'muts', 'beanies', 'bucket-hat', 'trucker-hat', 'hats', 'caps'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['gelato', 'printful', 'manual'],
    copy: basicCopy(
      'Caps & mutsen',
      'Caps en mutsen bedrukken',
      'Caps, mutsen, snapbacks en hoeden met eigen ontwerp of logo.',
      'Caps en mutsen bedrukken | Printen.eu',
      'Bestel caps, mutsen, snapbacks en hoeden met eigen logo of ontwerp.',
    ),
  },
  {
    slug: 'jassen-bodywarmers',
    aliases: ['jassen', 'jas', 'softshell', 'windbreaker', 'bodywarmer', 'jackets'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['printcom', 'gelato', 'printful', 'manual'],
    copy: copy(
      {
        label: 'Jassen & bodywarmers',
        title: 'Jassen en bodywarmers bedrukken',
        subtitle: 'Softshells, windjacks en bodywarmers met eigen logo of ontwerp voor team, bedrijf en evenement.',
        metaTitle: 'Jassen en bodywarmers bedrukken | Printen.eu',
        metaDescription: 'Bestel softshell jassen, windjacks en bodywarmers met eigen logo, naam of ontwerp. Voor bedrijfskleding, teams en events.',
      },
      {
        label: 'Jacken & Bodywarmer',
        title: 'Jacken und Bodywarmer bedrucken',
        subtitle: 'Softshelljacken, Windjacken und Bodywarmer mit eigenem Logo oder Design für Team, Firma und Event.',
        metaTitle: 'Jacken und Bodywarmer bedrucken | Printen.eu',
        metaDescription: 'Softshelljacken, Windjacken und Bodywarmer mit eigenem Logo, Namen oder Design bestellen. Für Firmenkleidung, Teams und Events.',
      },
      {
        label: 'Jackets & bodywarmers',
        title: 'Custom printed jackets and bodywarmers',
        subtitle: 'Softshells, windbreakers and bodywarmers with your own logo or design for teams, companies and events.',
        metaTitle: 'Custom jackets and bodywarmers | Printen.eu',
        metaDescription: 'Order softshell jackets, windbreakers and bodywarmers with your own logo, name or design. For workwear, teams and events.',
      },
      {
        label: 'Vestes & bodywarmers',
        title: 'Vestes et bodywarmers personnalisés',
        subtitle: 'Softshells, coupe-vent et bodywarmers avec votre logo ou visuel pour équipe, entreprise et événement.',
        metaTitle: 'Vestes et bodywarmers personnalisés | Printen.eu',
        metaDescription: 'Commandez softshells, coupe-vent et bodywarmers avec votre logo, nom ou visuel. Pour vêtements de travail, équipes et événements.',
      },
    ),
  },
  {
    slug: 'sokken-slippers',
    aliases: ['sokken', 'sok', 'slippers', 'slipper', 'socks', 'flip-flops'],
    parentSlug: 'kleding',
    group: 'textile',
    providers: ['printcom', 'gelato', 'printful', 'manual'],
    copy: copy(
      {
        label: 'Sokken & slippers',
        title: 'Sokken en slippers bedrukken',
        subtitle: 'Sokken met eigen logo of all-over print en bedrukte slippers voor team, merch en giveaway.',
        metaTitle: 'Sokken en slippers bedrukken | Printen.eu',
        metaDescription: 'Bestel sokken met eigen logo of all-over print en slippers met eigen ontwerp. Voor merchandise, teams, events en giveaways.',
      },
      {
        label: 'Socken & Badelatschen',
        title: 'Socken und Badelatschen bedrucken',
        subtitle: 'Socken mit eigenem Logo oder Allover-Druck und bedruckte Badelatschen für Team, Merch und Giveaway.',
        metaTitle: 'Socken und Badelatschen bedrucken | Printen.eu',
        metaDescription: 'Socken mit eigenem Logo oder Allover-Druck und Badelatschen mit eigenem Design bestellen. Für Merchandise, Teams und Events.',
      },
      {
        label: 'Socks & slippers',
        title: 'Custom printed socks and slippers',
        subtitle: 'Socks with your own logo or all-over print and printed slippers for teams, merch and giveaways.',
        metaTitle: 'Custom socks and slippers | Printen.eu',
        metaDescription: 'Order socks with your own logo or all-over print and slippers with your own design. For merchandise, teams, events and giveaways.',
      },
      {
        label: 'Chaussettes & tongs',
        title: 'Chaussettes et tongs personnalisées',
        subtitle: 'Chaussettes avec votre logo ou impression intégrale et tongs imprimées pour équipe, merch et cadeaux.',
        metaTitle: 'Chaussettes et tongs personnalisées | Printen.eu',
        metaDescription: 'Commandez des chaussettes avec votre logo ou impression intégrale et des tongs à votre image. Pour merchandising, équipes et événements.',
      },
    ),
  },
  {
    slug: 'telefoonhoesjes',
    aliases: ['phone-cases', 'telefoonhoesje', 'snap-case', 'tough-case'],
    parentSlug: 'technologie',
    group: 'technology',
    providers: ['prodigi', 'gelato', 'printful', 'manual'],
    copy: basicCopy(
      'Telefoonhoesjes',
      'Telefoonhoesjes bedrukken',
      'Telefoonhoesjes en smartphone cases met eigen foto, patroon of ontwerp.',
      'Telefoonhoesjes bedrukken | Printen.eu',
      'Bestel telefoonhoesjes met eigen foto, logo, patroon of ontwerp.',
    ),
  },
  {
    slug: 'muismatten-gaming',
    aliases: ['mouse-mats', 'muismat', 'gaming-mats', 'desk-mats'],
    parentSlug: 'technologie',
    group: 'technology',
    providers: ['prodigi', 'gelato', 'manual'],
    copy: basicCopy(
      'Muismatten & gaming',
      'Muismatten en gaming mats bedrukken',
      'Muismatten, gaming mats en bureaumatten met eigen ontwerp.',
      'Muismatten en gaming mats | Printen.eu',
      'Bestel muismatten, gaming mats en bureaumatten met eigen ontwerp.',
    ),
  },
  {
    slug: 'laptop-tablet-accessoires',
    aliases: ['laptop-sleeve', 'laptop-sleeves', 'tablet-cases', 'ipad-case'],
    parentSlug: 'technologie',
    group: 'technology',
    providers: ['prodigi', 'gelato', 'manual'],
    copy: basicCopy(
      'Laptop & tablet',
      'Laptop- en tabletaccessoires bedrukken',
      'Laptop sleeves, tablet cases en accessoires met eigen ontwerp.',
      'Laptop en tablet accessoires | Printen.eu',
      'Bestel laptop sleeves en tabletaccessoires met eigen print of ontwerp.',
    ),
  },
  {
    slug: 'ophangsystemen',
    aliases: ['canvas-ophangsysteem', 'blind-ophangsysteem', 'ophangplaat', 'ophangoog', 'verstelbare-ophangkabel', 'plakhanger'],
    parentSlug: 'accessoires',
    group: 'accessories',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Ophangsystemen',
      'Ophangsystemen en montage',
      'Ophangplaten, kabels, ophangogen en systemen voor panelen, canvas en frames.',
      'Ophangsystemen bestellen | Printen.eu',
      'Bestel ophangsystemen en montageaccessoires voor printproducten.',
    ),
  },
  {
    slug: 'afstandhouders',
    aliases: ['afstandhouder', 'nylon-afstandhouder', 'hexagon-afstandhouder', 'lijm-voor-afstandhouder', 'spacers'],
    parentSlug: 'accessoires',
    group: 'accessories',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Afstandhouders',
      'Afstandhouders bestellen',
      'Afstandhouders, zijgriphouders en montage voor platen en panelen.',
      'Afstandhouders bestellen | Printen.eu',
      'Bestel afstandhouders en montageaccessoires voor platen, panelen en wandprints.',
    ),
  },
  {
    slug: 'banner-accessoires',
    aliases: ['spanner-met-spinhaak', 'stormfix-spanner', 'tiewrap', 'karabijnhaak', 'plafondhaak', 'zuignap-met-haak'],
    parentSlug: 'accessoires',
    group: 'accessories',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Banner accessoires',
      'Banner- en spandoekaccessoires',
      'Spanners, haken, tiewraps en montageaccessoires voor spandoeken en banners.',
      'Banner accessoires bestellen | Printen.eu',
      'Bestel spandoekaccessoires, haken, spanners en bevestiging voor banners.',
    ),
  },
  {
    slug: 'standaards-houders',
    aliases: ['bamboe-standaard', 'barok-standaard', 'basic-standaard', 'mini-standaardjes', 'stands', 'holders'],
    parentSlug: 'accessoires',
    group: 'accessories',
    providers: ['manual', 'printcom', 'probo'],
    copy: basicCopy(
      'Standaards & houders',
      'Standaards en houders',
      'Standaards, mini-standaards en houders voor tegels, panelen en kleine displays.',
      'Standaards en houders | Printen.eu',
      'Bestel standaards en houders voor fototegels, panelen en kleine printproducten.',
    ),
  },
  {
    slug: 'relatiegeschenken',
    aliases: ['gifts-merchandise-promotional', 'promotional-products', 'merchandise', 'gadgets'],
    group: 'gifts',
    providers: ['printcom', 'prodigi', 'gelato', 'manual'],
    copy: basicCopy(
      'Relatiegeschenken',
      'Relatiegeschenken en promotieartikelen',
      'Promotieartikelen, giveaways, gadgets en bedrukte merchandise voor campagnes en klanten.',
      'Relatiegeschenken bedrukken | Printen.eu',
      'Bestel relatiegeschenken, promotieartikelen en merchandise met eigen logo of ontwerp.',
    ),
  },
  {
    slug: 'pennen-schrijfwaren',
    aliases: ['pens', 'pennen', 'potloden', 'writing-instruments'],
    parentSlug: 'relatiegeschenken',
    group: 'gifts',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Pennen & schrijfwaren',
      'Pennen en schrijfwaren bedrukken',
      'Pennen, potloden, markeerstiften en schrijfwaren als promotieartikel.',
      'Pennen bedrukken | Printen.eu',
      'Bestel pennen, potloden en schrijfwaren met eigen logo of campagneboodschap.',
    ),
  },
  {
    slug: 'sleutelhangers-keycords',
    aliases: ['keycords', 'sleutelhangers', 'lanyards', 'keyrings'],
    parentSlug: 'relatiegeschenken',
    group: 'gifts',
    providers: ['printcom', 'prodigi', 'manual'],
    copy: basicCopy(
      'Sleutelhangers & keycords',
      'Sleutelhangers en keycords bedrukken',
      'Sleutelhangers, keycords en lanyards met logo voor event, beurs en promotie.',
      'Sleutelhangers en keycords bedrukken | Printen.eu',
      'Bestel sleutelhangers en keycords met eigen logo, tekst of ontwerp.',
    ),
  },
  {
    slug: 'paraplus-regen',
    aliases: ['paraplu', 'paraplus', 'umbrellas', 'regenponcho'],
    parentSlug: 'relatiegeschenken',
    group: 'gifts',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Paraplu’s & regen',
      'Paraplu’s en regenartikelen bedrukken',
      'Paraplu’s, poncho’s en weerbestendige promotieartikelen met logo.',
      'Paraplu’s bedrukken | Printen.eu',
      'Bestel paraplu’s en regenartikelen met eigen logo of merkontwerp.',
    ),
  },
  {
    slug: 'snoep-eten-drinken',
    aliases: ['food-gifts', 'sweets', 'chocolate', 'easter', 'drinks', 'horeca-print'],
    parentSlug: 'relatiegeschenken',
    group: 'gifts',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Snoep, eten & drinken',
      'Eten en drinken met bedrukking',
      'Snoep, chocolade, drankjes, koekjes en food giveaways met merkuitstraling.',
      'Eten en drinken als relatiegeschenk | Printen.eu',
      'Bestel snoep, chocolade, drankjes en food promotieartikelen met eigen sleeve, label of logo.',
    ),
  },
  {
    slug: 'glaswerk-drinkflessen',
    aliases: ['drinkingbottles', 'drinking-cups', 'drinkware', 'shot-glasses', 'bottles', 'cups', 'glasses'],
    parentSlug: 'drinkwaren',
    group: 'gifts',
    providers: ['printcom', 'prodigi', 'gelato', 'manual'],
    copy: basicCopy(
      'Glaswerk & drinkflessen',
      'Glaswerk en drinkflessen bedrukken',
      'Drinkflessen, glazen, bekers, bidons en tumblers met logo of ontwerp.',
      'Glaswerk en drinkflessen bedrukken | Printen.eu',
      'Bestel drinkflessen, glazen, bekers en tumblers met eigen logo of ontwerp.',
    ),
  },
  {
    slug: 'kantoor-promotie',
    aliases: ['office-stationery', 'office-print', 'stationery', 'desk-items'],
    parentSlug: 'relatiegeschenken',
    group: 'gifts',
    providers: ['printcom', 'gelato', 'manual'],
    copy: basicCopy(
      'Kantoor & promotie',
      'Kantoorartikelen bedrukken',
      'Kantoorartikelen, bureauproducten, memoblokken en praktische promotieartikelen.',
      'Kantoorartikelen bedrukken | Printen.eu',
      'Bestel kantoorartikelen en bureauproducten met eigen logo of ontwerp.',
    ),
  },
  {
    slug: 'stempels',
    aliases: ['stamps', 'stempel', 'colop', 'trodat'],
    parentSlug: 'kantoor-promotie',
    group: 'gifts',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Stempels',
      'Stempels bestellen',
      'Stempels, datumstempels en zakelijke stempelproducten voor kantoor en balie.',
      'Stempels bestellen | Printen.eu',
      'Bestel stempels en stempelproducten met eigen tekst, logo of bedrijfsgegevens.',
    ),
  },
  {
    slug: 'usb-powerbanks',
    aliases: ['usb-sticks', 'usb-stick', 'powerbank', 'powerbanks', 'usb-hub'],
    parentSlug: 'technologie',
    group: 'technology',
    providers: ['printcom', 'prodigi', 'manual'],
    copy: basicCopy(
      'USB & powerbanks',
      'USB-sticks en powerbanks bedrukken',
      'USB-sticks, powerbanks, hubs en elektronische giveaways met logo.',
      'USB-sticks en powerbanks bedrukken | Printen.eu',
      'Bestel USB-sticks, powerbanks en tech giveaways met eigen logo.',
    ),
  },
  {
    slug: 'polsbandjes-event',
    aliases: ['wristbands', 'polsbandjes', 'plastic-polsbandje', 'vinyl-polsbandje', 'tyvek-polsbandje'],
    parentSlug: 'beurs-evenement',
    group: 'exhibition',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Polsbandjes',
      'Polsbandjes voor events',
      'Polsbandjes voor festivals, events, toegang, sportdagen en campagnes.',
      'Polsbandjes bedrukken | Printen.eu',
      'Bestel polsbandjes met eigen tekst, kleur of ontwerp voor event en toegang.',
    ),
  },
  {
    slug: 'horeca-menu',
    aliases: ['menus', 'menukaarten', 'menumap', 'biervilt', 'placemat', 'servet', 'napkins'],
    parentSlug: 'paper-print',
    group: 'print',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Horeca drukwerk',
      'Horeca drukwerk bestellen',
      'Menukaarten, bierviltjes, placemats, servetten en horeca printproducten.',
      'Horeca drukwerk bestellen | Printen.eu',
      'Bestel horeca drukwerk zoals menukaarten, bierviltjes, placemats en servetten.',
    ),
  },
  {
    slug: 'stickers-folies',
    aliases: ['folie', 'folies', 'snijfolie', 'raamsticker', 'vloersticker', 'carwrap', 'one-way-vision', 'magneetfolie'],
    parentSlug: 'stickers-tapes',
    group: 'stickers',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Folies & raamstickers',
      'Folies en raamstickers bedrukken',
      'Raamstickers, snijfolie, vloerstickers, carwrap en specialistische folies.',
      'Folies en raamstickers | Printen.eu',
      'Bestel folies, raamstickers, vloerstickers en carwrap materiaal met eigen ontwerp.',
    ),
  },
  {
    slug: 'verzendverpakkingen',
    aliases: ['shipping-packaging', 'verzenddoos', 'brievenbusdoos', 'verzendzak', 'mailers'],
    parentSlug: 'verpakkingen',
    group: 'packaging',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Verzendverpakkingen',
      'Verzendverpakkingen bedrukken',
      'Verzenddozen, brievenbusdozen, verzendzakken en mailers met eigen merk.',
      'Verzendverpakkingen bedrukken | Printen.eu',
      'Bestel verzendverpakkingen, verzenddozen en mailers met eigen logo of ontwerp.',
    ),
  },
  {
    slug: 'dozen-verpakkingen',
    aliases: ['boxes', 'dozen', 'foodbox', 'schuifdoos', 'telescoopdoos', 'gift-packaging'],
    parentSlug: 'verpakkingen',
    group: 'packaging',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Dozen & verpakkingen',
      'Dozen en verpakkingen bedrukken',
      'Productdozen, cadeauverpakkingen, schuifdozen en kartonnen verpakkingen.',
      'Dozen en verpakkingen bedrukken | Printen.eu',
      'Bestel dozen en verpakkingen met eigen bedrukking voor retail, cadeau en verzending.',
    ),
  },
  {
    slug: 'tassen-verpakkingen',
    aliases: ['packaging-bags', 'promotional-bags', 'papieren-tas', 'katoenen-tas', 'jute-tas', 'shopper'],
    parentSlug: 'verpakkingen',
    group: 'packaging',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Tassen & shoppers',
      'Tassen en shoppers bedrukken',
      'Papieren tassen, katoenen tassen, shoppers, rugzakken en draagtassen voor retail.',
      'Tassen en shoppers bedrukken | Printen.eu',
      'Bestel papieren tassen, katoenen tassen, shoppers en rugzakken met eigen logo.',
    ),
  },
  {
    slug: 'horeca-verpakkingen',
    aliases: ['take-away', 'food-packaging', 'snackbox', 'pokebowl', 'ijsbeker', 'koffiebeker'],
    parentSlug: 'verpakkingen',
    group: 'packaging',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Horeca verpakkingen',
      'Horeca verpakkingen bedrukken',
      'Take-away verpakkingen, bekers, snackbakjes, foodboxen en servetten.',
      'Horeca verpakkingen bedrukken | Printen.eu',
      'Bestel horeca verpakkingen, bekers en take-away materialen met eigen bedrukking.',
    ),
  },
  {
    slug: 'tape-lint',
    aliases: ['bedrukte-tape', 'tape', 'lint', 'bedrukt-lint', 'gesneden-lint'],
    parentSlug: 'verpakkingen',
    group: 'packaging',
    providers: ['printcom', 'manual'],
    copy: basicCopy(
      'Tape & lint',
      'Tape en lint bedrukken',
      'Bedrukte tape, lint en verpakkingsafwerking met logo of tekst.',
      'Tape en lint bedrukken | Printen.eu',
      'Bestel bedrukte tape en lint met eigen logo, tekst of merkontwerp.',
    ),
  },
  {
    slug: 'materiaal-samples',
    aliases: ['materials-samples', 'samples', 'sample', 'lookbook', 'kleurenwaaier'],
    parentSlug: 'plaatmateriaal',
    group: 'materials',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Materiaal samples',
      'Materiaal samples en waaiers',
      'Samples, lookbooks, kleurenwaaiers en materiaalvoorbeelden voor printkeuze.',
      'Materiaal samples bestellen | Printen.eu',
      'Bestel materiaal samples, lookbooks en kleurenwaaiers voor print- en materiaalkeuze.',
    ),
  },
  {
    slug: 'bannermaterialen',
    aliases: ['frontlit-banner', 'mesh-banner', 'blockout-banner', 'banner-materials', 'spandoekmateriaal'],
    parentSlug: 'textielframes-doeken',
    group: 'materials',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Bannermaterialen',
      'Bannermaterialen bedrukken',
      'Frontlit, mesh, blockout en andere materialen voor banners en spandoeken.',
      'Bannermaterialen bedrukken | Printen.eu',
      'Bestel bannermaterialen en spandoekmaterialen met eigen print.',
    ),
  },
  {
    slug: 'textielmaterialen',
    aliases: ['textile-materials', 'decotex', 'blackback', 'backlit', 'flag-pet', 'spunpolyester'],
    parentSlug: 'textielframes-doeken',
    group: 'materials',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Textielmaterialen',
      'Textielmaterialen bedrukken',
      'Textiel, vlagdoek, backlit, blackback en stoffen voor frames en presentaties.',
      'Textielmaterialen bedrukken | Printen.eu',
      'Bestel textielmaterialen en stoffen met eigen print voor frame, vlag of presentatie.',
    ),
  },
  {
    slug: 'outdoor-meubilair',
    aliases: ['outdoor-furniture', 'strandstoel', 'regisseursstoel', 'hangmat', 'parasol'],
    parentSlug: 'outdoor-advertising',
    group: 'outdoor',
    providers: ['printcom', 'probo', 'manual'],
    copy: basicCopy(
      'Outdoor meubilair',
      'Outdoor meubilair bedrukken',
      'Strandstoelen, parasols, hangmatten en outdoor producten met eigen merk.',
      'Outdoor meubilair bedrukken | Printen.eu',
      'Bestel outdoor meubilair en strandproducten met eigen logo of ontwerp.',
    ),
  },
  {
    slug: 'sport-promotie',
    aliases: ['sportshirt', 'sporthanddoek', 'traininghesje', 'sportveldbeugel', 'sport-games'],
    parentSlug: 'sport-spel',
    group: 'sports',
    providers: ['printcom', 'prodigi', 'manual'],
    copy: basicCopy(
      'Sportpromotie',
      'Sportproducten bedrukken',
      'Sportshirts, hesjes, sporthanddoeken, bidons en promotie voor clubs en events.',
      'Sportproducten bedrukken | Printen.eu',
      'Bestel sportproducten en clubpromotie met eigen logo, naam of ontwerp.',
    ),
  },
  {
    slug: 'spellen-puzzels',
    aliases: ['games', 'legpuzzel', 'puzzel', 'speelkaarten', 'kwartetspel', 'memorykaart', 'frisbee'],
    parentSlug: 'sport-spel',
    group: 'sports',
    providers: ['printcom', 'prodigi', 'manual'],
    copy: basicCopy(
      'Spellen & puzzels',
      'Spellen en puzzels personaliseren',
      'Puzzels, speelkaarten, spellen en promotionele games met eigen ontwerp.',
      'Spellen en puzzels personaliseren | Printen.eu',
      'Bestel spellen, puzzels en speelkaarten met eigen foto, logo of ontwerp.',
    ),
  },
  ...([
    ['promotionele-gadgets', 'Promotionele gadgets', 'Promotionele gadgets bedrukken', 'Praktische giveaways en kleine gadgets met logo of ontwerp.', 'relatiegeschenken', 'gifts'],
    ['verzorging-wellness', 'Verzorging & wellness', 'Verzorgingsproducten personaliseren', 'Verzorging, zonbescherming en wellnessproducten als relatiegeschenk.', 'relatiegeschenken', 'gifts'],
    ['feest-seizoen', 'Feest & seizoen', 'Feest- en seizoensproducten bedrukken', 'Producten voor kerst, Pasen, geslaagd, feestdagen en acties.', 'relatiegeschenken', 'gifts'],
    ['gereedschap-auto', 'Gereedschap & auto', 'Gereedschap en auto-accessoires bedrukken', 'Praktische gereedschappen en auto-accessoires als promotioneel product.', 'relatiegeschenken', 'gifts'],
    ['montage-afwerking', 'Montage & afwerking', 'Montage- en afwerkingsaccessoires', 'Hulpmiddelen, lijm, tape, rakels en onderdelen voor professionele montage.', 'accessoires', 'accessories'],
    ['sjaals-handschoenen', 'Sjaals & handschoenen', 'Sjaals en handschoenen bedrukken', 'Sjaals, cols, handschoenen en winteraccessoires met eigen ontwerp.', 'textiel-cadeaus', 'textile'],
    ['kantoor-drukwerk', 'Kantoor- & formulierdrukwerk', 'Kantoor- en formulierdrukwerk bestellen', 'Formulieren, sets, tabbladen, ringbanden en praktisch kantoor drukwerk.', 'paper-print', 'print'],
    ['pos-presentatie', 'POS & presentatie', 'POS- en presentatiematerialen bedrukken', 'Balies, displays, folderhouders, kubussen en winkelpresentaties.', 'displays-pos', 'display'],
    ['event-tenten', 'Tenten & eventmateriaal', 'Tenten en eventmateriaal bedrukken', 'Tenten, wanden, gewichten en accessoires voor buitenactiviteiten en events.', 'beurs-evenement', 'exhibition'],
  ] as const).map(([slug, label, title, subtitle, parentSlug, group]) => ({
    slug,
    parentSlug,
    group,
    providers: ['printcom', 'probo', 'prodigi', 'gelato', 'printful', 'manual'] as CategoryDefinition['providers'],
    copy: basicCopy(label, title, subtitle, `${title} | Printen.eu`, `${subtitle} Bestel online bij Printen.eu.`),
  })),
];

// Categorieën met alleen Nederlandse basicCopy krijgen hun de/en/fr-teksten uit category-copy-translations.ts.
for (const definition of categoryTaxonomy) {
  const translated = categoryCopyTranslations[definition.slug];
  if (!translated) continue;
  for (const locale of ['de', 'en', 'fr'] as const) {
    const localized = translated[locale];
    if (!localized || definition.copy[locale].subtitle !== definition.copy.nl.subtitle) continue;
    definition.copy = { ...definition.copy, [locale]: { ...definition.copy[locale], ...localized } };
  }
}

export const categoryGroupLabels: Record<CategoryDefinition['group'], Record<CategoryLocale, string>> = {
  'photo-wall': { nl: 'Foto & wand', de: 'Foto & Wand', en: 'Photo & wall', fr: 'Photo & mur' },
  stickers: { nl: 'Stickers', de: 'Sticker', en: 'Stickers', fr: 'Stickers' },
  outdoor: { nl: 'Buiten', de: 'Außen', en: 'Outdoor', fr: 'Extérieur' },
  flags: { nl: 'Vlaggen', de: 'Flaggen', en: 'Flags', fr: 'Drapeaux' },
  exhibition: { nl: 'Beurs & event', de: 'Messe & Event', en: 'Exhibition', fr: 'Salon & event' },
  display: { nl: 'Displays & POS', de: 'Displays & POS', en: 'Displays & POS', fr: 'Displays & PLV' },
  materials: { nl: 'Materialen', de: 'Materialien', en: 'Materials', fr: 'Materiaux' },
  print: { nl: 'Drukwerk', de: 'Drucksachen', en: 'Print', fr: 'Imprimes' },
  interior: { nl: 'Interieur', de: 'Interieur', en: 'Interior', fr: 'Interieur' },
  textile: { nl: 'Kleding', de: 'Bekleidung', en: 'Apparel', fr: 'Vetements' },
  gifts: { nl: 'Cadeaus', de: 'Geschenke', en: 'Gifts', fr: 'Cadeaux' },
  packaging: { nl: 'Verpakkingen', de: 'Verpackungen', en: 'Packaging', fr: 'Emballages' },
  technology: { nl: 'Tech', de: 'Tech', en: 'Tech', fr: 'Tech' },
  books: { nl: 'Boeken', de: 'Bücher', en: 'Books', fr: 'Livres' },
  sports: { nl: 'Sport & spel', de: 'Sport & Spiele', en: 'Sport & games', fr: 'Sport & jeux' },
  accessories: { nl: 'Accessoires', de: 'Zubehör', en: 'Accessories', fr: 'Accessoires' },
};

// Apply at the source so direct taxonomy consumers, navigation and SEO agree.
// basicCopy historically copied Dutch text into every locale.
for (const definition of categoryTaxonomy) {
  const french = getReviewedFrenchCategoryCopy(definition.slug);
  if (french) definition.copy.fr = french;
}

const definitionsBySlug = new Map(categoryTaxonomy.map((definition) => [definition.slug, definition]));
const aliases = new Map<string, string>();

categoryTaxonomy.forEach((definition) => {
  aliases.set(definition.slug.toLowerCase(), definition.slug);
  definition.aliases?.forEach((alias) => aliases.set(alias.toLowerCase(), definition.slug));
});

export function normalizeCategoryLocale(locale = 'nl'): CategoryLocale {
  return locale === 'de' || locale === 'en' || locale === 'fr' ? locale : 'nl';
}

export function getCategoryDefinition(category: string | null | undefined) {
  if (!category) return null;
  const canonical = aliases.get(category.toLowerCase()) || category;
  return definitionsBySlug.get(canonical) || null;
}

export function getCanonicalCategory(category: string) {
  return getCategoryDefinition(category)?.slug || category;
}

export function getCategoryFilterValues(category: string) {
  const definition = getCategoryDefinition(category);
  return definition ? [definition.slug, ...(definition.aliases || [])] : [category];
}

/** De categorie zelf plus alle onderliggende categorieën en hun aliases. */
export function getCategoryTreeFilterValues(category: string) {
  const canonical = getCanonicalCategory(category);
  const included = new Set<string>(getCategoryFilterValues(canonical));
  let changed = true;

  while (changed) {
    changed = false;
    for (const definition of categoryTaxonomy) {
      if (!definition.parentSlug || !included.has(definition.parentSlug)) continue;
      for (const value of [definition.slug, ...(definition.aliases || [])]) {
        if (!included.has(value)) { included.add(value); changed = true; }
      }
    }
  }

  return Array.from(included);
}

export function getAllCategoryFilterValues() {
  return categoryTaxonomy.flatMap((definition) => [definition.slug, ...(definition.aliases || [])]);
}

export function getCategoryCopy(category: string, locale = 'nl') {
  const definition = getCategoryDefinition(category);
  if (!definition) return null;
  return definition.copy[normalizeCategoryLocale(locale)] || definition.copy.nl;
}

function buildDefaultSeoSections(definition: CategoryDefinition, locale: CategoryLocale): CategorySeoSection[] {
  const categoryCopy = definition.copy[locale] || definition.copy.nl;
  return buildLocalizedDefaultSeoSections(categoryCopy, locale);
}


function countSeoWords(sections: CategorySeoSection[]) {
  return sections
    .flatMap((section) => [section.heading, section.body])
    .join(' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

export function getCategorySeoContent(category: string, locale = 'nl') {
  const definition = getCategoryDefinition(category);
  if (!definition) return null;
  const normalizedLocale = normalizeCategoryLocale(locale);
  const categoryCopy = definition.copy[normalizedLocale] || definition.copy.nl;
  const defaultSections = buildDefaultSeoSections(definition, normalizedLocale);
  const customSections = categoryCopy.seoSections || [];
  const sections = customSections.length > 0 && countSeoWords(customSections) >= 650
    ? customSections
    : defaultSections;

  return {
    heading: categoryCopy.seoHeading || categoryCopy.metaTitle.replace(' | Printen.eu', ''),
    sections,
  };
}

export function formatCategory(category: string, locale = 'nl') {
  const categoryCopy = getCategoryCopy(category, locale);
  return categoryCopy?.label || category.replace(/-/g, ' ');
}
